import { useEffect, useMemo, useState, createContext, useContext, useRef } from "react";
import axios from "axios";
import {
  Banknote, Bell, BriefcaseBusiness, Camera, Check, ChevronRight, ClipboardList, CreditCard, Download, FileText, Image as ImageIcon,
  LayoutDashboard, LogOut, MapPin, Menu, Navigation, Package, Pencil, Percent, Phone, Plus, Radio, RefreshCw, ScanLine,
  Search, Send, ShoppingCart, TrendingUp, Trash2, UserPlus, Users, Wallet, X,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@/App.css";
import "@/Mobile.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
const money = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const initials = (s = "") => s.split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.msg) return d.msg;
  return e?.message || "Bir hata oluştu";
};

const greetingFor = (name = "", d = new Date()) => {
  // İstanbul saatine göre karşılama (UTC+3, DST yok)
  const istHour = (d.getUTCHours() + 3) % 24;
  let g = "İyi geceler";
  if (istHour >= 5 && istHour < 12) g = "Günaydın";
  else if (istHour >= 12 && istHour < 18) g = "İyi günler";
  else if (istHour >= 18 && istHour < 22) g = "İyi akşamlar";
  const first = (name || "").split(" ")[0] || "";
  return first ? `${g}, ${first}` : g;
};

// ============ Auth Context ============
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, {}=logged in
  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(false);
    // Belt-and-suspenders: verify server-side session is gone; if not, reload once.
    try {
      await axios.get(`${API}/auth/me`);
      // If /me still succeeds, cookies weren't cleared — force a hard reload.
      window.location.reload();
    } catch {
      // 401 as expected — logout succeeded
    }
  };
  const updateProfile = async (payload) => {
    const { data } = await axios.put(`${API}/auth/me`, payload);
    setUser(data);
    return data;
  };
  const value = useMemo(() => ({ user, setUser, login, logout, updateProfile }), [user]); // eslint-disable-line react-hooks/exhaustive-deps
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// ============ Notifications Bell ============
function playPing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    // Two-tone "ding-dong"
    [{ f: 880, t: 0 }, { f: 660, t: 0.18 }].forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.35);
    });
    setTimeout(() => ctx.close(), 800);
  } catch {}
}

function showBrowserPush(title, body) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const n = new Notification(title, { body, icon: "/favicon.ico", tag: "lokalnetbt", silent: true });
    setTimeout(() => n.close(), 6000);
  } catch {}
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("lnb-notif-muted") === "1");
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/notifications`);
      const newItems = r.data.items || [];
      // Detect newly arrived notifications (not seen in previous poll)
      const fresh = newItems.filter((n) => !knownIdsRef.current.has(n.id) && !n.read);
      newItems.forEach((n) => knownIdsRef.current.add(n.id));
      if (!firstLoadRef.current && fresh.length > 0 && !muted) {
        playPing();
        const first = fresh[0];
        showBrowserPush(first.title, first.message);
      }
      firstLoadRef.current = false;
      setItems(newItems);
      setUnread(r.data.unread || 0);
    } catch {}
  };
  useEffect(() => {
    load();
    // Request notification permission on first mount
    if ("Notification" in window && Notification.permission === "default") {
      // Deferred to first user interaction to avoid annoying prompt on page load
      const onFirstClick = () => {
        Notification.requestPermission().catch(() => {});
        window.removeEventListener("click", onFirstClick);
      };
      window.addEventListener("click", onFirstClick, { once: true });
    }
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const markOne = async (id) => {
    try { await axios.post(`${API}/notifications/${id}/read`); load(); } catch {}
  };
  const markAll = async () => {
    try { await axios.post(`${API}/notifications/read-all`); load(); } catch {}
  };
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("lnb-notif-muted", next ? "1" : "0");
      return next;
    });
  };
  const iconOf = (kind) =>
    kind === "job_completed" ? <Check size={13} /> :
    kind === "job_created" ? <BriefcaseBusiness size={13} /> :
    kind === "low_stock" ? <Package size={13} /> :
    <Bell size={13} />;
  return (
    <div className="notif-wrap">
      <button className="icon-btn" data-testid="notifications-button" onClick={() => setOpen((o) => !o)} aria-label="Bildirimler">
        <Bell size={19} />
        {unread > 0 && <i data-testid="notif-unread-badge">{unread > 9 ? "9+" : unread}</i>}
      </button>
      {open && (
        <>
          <div className="notif-scrim" onClick={() => setOpen(false)}></div>
          <div className="notif-panel" data-testid="notif-panel">
            <div className="notif-head">
              <strong>Bildirimler</strong>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  data-testid="notif-mute-toggle"
                  className="text-btn small"
                  onClick={toggleMute}
                  title={muted ? "Sesi aç" : "Sesi kapat"}
                >
                  {muted ? "🔕" : "🔔"}
                </button>
                {items.length > 0 && (
                  <button data-testid="notif-read-all" className="text-btn" onClick={markAll}>Tümü okundu</button>
                )}
              </div>
            </div>
            {items.length === 0 ? (
              <div className="notif-empty">Henüz bildirim yok</div>
            ) : (
              <div className="notif-list">
                {items.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    className={"notif-item" + (n.read ? "" : " unread")}
                    onClick={() => markOne(n.id)}
                  >
                    <span className={"notif-icon " + n.kind}>{iconOf(n.kind)}</span>
                    <div className="notif-body">
                      <strong>{n.title}</strong>
                      <span>{n.message}</span>
                      <em>{new Date(n.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</em>
                    </div>
                    {!n.read && <span className="notif-dot"></span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ============ Confirm Modal (themed replacement for window.confirm) ============
function ConfirmModal({ open, title, message, confirmLabel = "Onayla", cancelLabel = "İptal", danger = false, icon: Icon, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className={"confirm-icon" + (danger ? " danger" : "")}>
          {Icon ? <Icon size={22} /> : <Trash2 size={22} />}
        </div>
        <h2>{title}</h2>
        <p>{message}</p>
        <div className="confirm-actions">
          <button className="outline-btn wide" data-testid="confirm-cancel-button" onClick={onCancel}>{cancelLabel}</button>
          <button
            className={danger ? "danger-btn wide" : "primary wide"}
            data-testid="confirm-ok-button"
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============ Idle Timeout Hook ============
// 1 saat hareketsizlik → otomatik çıkış. 55. dakikada uyarı gösterir.
// Ref-based state so re-renders don't reset the timer.
function useIdleLogout(user, logout, onWarn) {
  const logoutRef = useRef(logout);
  const onWarnRef = useRef(onWarn);
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  useEffect(() => { onWarnRef.current = onWarn; }, [onWarn]);

  useEffect(() => {
    if (!user || !user.id) return;
    const TIMEOUT_MS = 60 * 60 * 1000; // 1 saat
    const WARN_MS = 55 * 60 * 1000;    // 55 dk
    let lastActivity = Date.now();
    let warned = false;
    const bump = () => {
      lastActivity = Date.now();
      warned = false;
    };
    const events = ["mousemove", "mousedown", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const check = setInterval(() => {
      const idle = Date.now() - lastActivity;
      if (idle >= TIMEOUT_MS) {
        clearInterval(check);
        events.forEach((e) => window.removeEventListener(e, bump));
        logoutRef.current && logoutRef.current();
      } else if (idle >= WARN_MS && !warned) {
        warned = true;
        onWarnRef.current && onWarnRef.current();
      }
    }, 20000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(check);
    };
    // Only re-mount when user identity changes, not on every parent re-render
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}


// ============ Live Location Ping Hook (staff) ============
function useStaffLiveTracking(user, jobs) {
  useEffect(() => {
    if (!user || user.role !== "staff" || !navigator.geolocation) return;
    const isAssigned = (j) => (j.staff_ids || []).includes(user.id) || (j.claimed_by_ids || []).includes(user.id) || j.staff_id === user.id || j.staff === user.name;
    const active = jobs.filter((j) => j.status === "Devam ediyor" && isAssigned(j));
    if (active.length === 0) return;

    const sendPos = (pos) => {
      active.forEach((job) => {
        axios.post(`${API}/jobs/${job.id}/location`, {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }).catch(() => {});
      });
    };
    // Bir kez hemen gönder
    navigator.geolocation.getCurrentPosition(sendPos, () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 });
    // watchPosition sürekli akış — cihaz hareket ettikçe otomatik trigger
    let watchId = null;
    try {
      watchId = navigator.geolocation.watchPosition(sendPos, () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 });
    } catch { /* ignore */ }
    // Yedek: 8s'de bir manuel poll (watchPosition tetiklenmese bile)
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(sendPos, () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 });
    }, 8000);
    return () => {
      clearInterval(interval);
      if (watchId != null) { try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, jobs]);
}

// ============ Track Modal (admin view of staff live location) ============
function TrackModal({ job, onClose }) {
  const [track, setTrack] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/jobs/${job.id}/track`);
      setTrack(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000); // refresh every 5s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    // fix default marker icons (leaflet + webpack quirk)
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    });
    const initial = [job.latitude || 41.0082, job.longitude || 28.9784];
    mapInstance.current = L.map(mapRef.current).setView(initial, 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(mapInstance.current);
    // Job destination marker (red pin)
    if (job.latitude && job.longitude) {
      const jobIcon = L.divIcon({
        className: "job-pin",
        html: '<div class="pin-inner"><span>İŞ</span></div>',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
      });
      L.marker([job.latitude, job.longitude], { icon: jobIcon }).addTo(mapInstance.current).bindPopup(`<b>${job.title}</b><br>${job.address}`);
    }
    return () => { mapInstance.current?.remove(); mapInstance.current = null; };
  }, [job]);

  const markersRef = useRef({});
  const polylinesRef = useRef({});

  useEffect(() => {
    if (!mapInstance.current || !track) return;
    const perStaff = track.latest_by_staff || (track.latest ? [track.latest] : []);
    // Group points by user_id for per-staff polylines
    const grouped = {};
    (track.points || []).forEach((p) => {
      const k = p.user_id || "unknown";
      (grouped[k] = grouped[k] || []).push([p.latitude, p.longitude]);
    });
    const staffColors = ["#2563eb", "#0d9f4a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"];
    // Remove stale
    Object.keys(polylinesRef.current).forEach((k) => {
      if (!grouped[k]) { polylinesRef.current[k].remove(); delete polylinesRef.current[k]; }
    });
    Object.keys(markersRef.current).forEach((k) => {
      if (!perStaff.find((p) => (p.user_id || "unknown") === k)) { markersRef.current[k].remove(); delete markersRef.current[k]; }
    });
    // Draw/update per staff
    Object.entries(grouped).forEach(([uid, coords], idx) => {
      const color = staffColors[idx % staffColors.length];
      if (polylinesRef.current[uid]) polylinesRef.current[uid].setLatLngs(coords);
      else polylinesRef.current[uid] = L.polyline(coords, { color, weight: 4, opacity: 0.75 }).addTo(mapInstance.current);
    });
    perStaff.forEach((p, idx) => {
      const uid = p.user_id || "unknown";
      const color = staffColors[idx % staffColors.length];
      const pos = [p.latitude, p.longitude];
      const html = `<div class="pin-inner staff" style="border-color:${color}"><span></span></div>`;
      const icon = L.divIcon({ className: "staff-pin", html, iconSize: [24, 24], iconAnchor: [12, 12] });
      const popup = `<b>${p.user_name || "Personel"}</b><br>${new Date(p.timestamp).toLocaleTimeString("tr-TR")}`;
      if (markersRef.current[uid]) { markersRef.current[uid].setLatLng(pos); markersRef.current[uid].setIcon(icon); markersRef.current[uid].bindPopup(popup); }
      else { markersRef.current[uid] = L.marker(pos, { icon }).addTo(mapInstance.current).bindPopup(popup); }
    });
    // Fit bounds
    const allCoords = [].concat(...Object.values(grouped));
    if (allCoords.length) {
      const bounds = L.latLngBounds(allCoords);
      if (job.latitude && job.longitude) bounds.extend([job.latitude, job.longitude]);
      mapInstance.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [track, job]);

  const latest = track?.latest;
  const points = track?.points || [];
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal track-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div>
            <span className="eyebrow">Canlı takip</span>
            <h2>{job.title}</h2>
            <p style={{ color: "#94a3b8", fontSize: 11, margin: "4px 0 0" }}>{job.staff} · {job.address}</p>
          </div>
          <button className="modal-close" data-testid="track-close-button" onClick={onClose}><X /></button>
        </div>
        <div className="track-stats">
          <div><span>Nokta</span><strong>{points.length}</strong></div>
          <div><span>Son güncelleme</span><strong>{latest ? new Date(latest.timestamp).toLocaleTimeString("tr-TR") : "—"}</strong></div>
          <div><span>Durum</span><strong>{track?.job?.status || "—"}</strong></div>
        </div>
        <div ref={mapRef} className="track-map" data-testid="track-map"></div>
        {!points.length && (
          <div className="track-empty">
            <Radio size={18} />
            Personel henüz canlı konum yollamadı. İş devam ederken telefonun konum izni açıksa yaklaşık 30 saniye içinde ilk sinyal düşer.
          </div>
        )}
      </div>
    </div>
  );
}


// ============ Login Page ============
function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email.trim().toLowerCase(), password); }
    catch (err) { setError(errText(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-brand">
          <div className="brand-mark">L</div>
          <div><strong>Lokalnet<span>BT</span></strong><small>Saha operasyon paneli</small></div>
        </div>
        <h1>Giriş yap</h1>
        <p>Hesabınızla giriş yapıp bugünkü iş akışınıza devam edin.</p>
        <form onSubmit={submit}>
          <label>E-posta<input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@lokalnetbt.com" /></label>
          <label>Şifre<input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></label>
          {error && <div className="login-error" data-testid="login-error">{error}</div>}
          <button data-testid="login-submit-button" className="primary wide" disabled={loading} type="submit">
            {loading ? "Giriş yapılıyor..." : "Giriş yap"}
          </button>
        </form>
        <div className="login-hint">
          <span>© {new Date().getFullYear()} LokalnetBT — Tüm hakları saklıdır</span>
        </div>
      </div>
      <div className="login-side">
        <div className="login-side-inner">
          <span className="eyebrow-light">LokalnetBT</span>
          <h2>Ekibinizin bugünkü işi tek ekranda.</h2>
          <p>İş planı, stok, satış ve fatura — sahada da masada da hızlı çalışır.</p>
          <div className="login-features">
            <div><BriefcaseBusiness size={17} /> Personel bazlı iş akışı</div>
            <div><Package size={17} /> Barkodlu stok ve satış</div>
            <div><FileText size={17} /> Fatura ve raporlar</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ App Shell ============
function App() {
  return <AuthProvider><Shell /></AuthProvider>;
}

function Shell() {
  const { user, logout, updateProfile } = useAuth();
  const [section, setSection] = useState("overview");
  const [jobs, setJobs] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [showJob, setShowJob] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [profitReport, setProfitReport] = useState(null);
  const [trackJob, setTrackJob] = useState(null);
  const [outOfRangeConfirm, setOutOfRangeConfirm] = useState(null); // { job, distance, allowed, resolve }
  const [editJob, setEditJob] = useState(null); // admin edit modal
  const [payments, setPayments] = useState([]);
  const [financeData, setFinanceData] = useState(null);

  const load = async () => {
    try {
      const [j, p, i, pay] = await Promise.all([
        axios.get(`${API}/jobs`), axios.get(`${API}/products`), axios.get(`${API}/invoices`),
        axios.get(`${API}/payments`),
      ]);
      setJobs(j.data); setProducts(p.data); setInvoices(i.data); setPayments(pay.data);
    } catch (e) { console.error(e); }
  };
  const loadAdminData = async () => {
    if (user?.role !== "admin") return;
    try {
      const [u, r, pr, fin] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/reports/summary`),
        axios.get(`${API}/reports/profit`),
        axios.get(`${API}/finance`),
      ]);
      setStaffUsers(u.data); setReport(r.data); setProfitReport(pr.data); setFinanceData(fin.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { if (user && user.id) { load(); loadAdminData(); } }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-refresh on section change so admin sees fresh data (e.g. after another session added products)
  useEffect(() => { if (user && user.id) { load(); if (user.role === "admin") loadAdminData(); } }, [section]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-refresh when the tab comes back into focus, or every 30s while active
  useEffect(() => {
    if (!user || !user.id) return;
    const refresh = () => { if (!document.hidden) { load(); if (user.role === "admin") loadAdminData(); } };
    const onFocus = () => refresh();
    const onVis = () => { if (!document.hidden) refresh(); };
    const interval = setInterval(refresh, 30000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useStaffLiveTracking(user, jobs);
  useIdleLogout(user, logout, () => {
    setToast("5 dakika sonra oturum kapanacak — hareket ederseniz uzatılır");
    setTimeout(() => setToast(""), 8000);
  });

  const subtotal = useMemo(() => cart.reduce((sum, i) => sum + i.price * i.qty, 0), [cart]);
  const tax = subtotal * 0.2;
  const total = subtotal + tax;

  if (user === null) return <div className="login-wrap"><div className="checking">Yükleniyor...</div></div>;
  if (user === false) return <Login />;

  const notify = (text) => { setToast(text); setTimeout(() => setToast(""), 2600); };

  const changeStatus = async (job, status, extra = {}) => {
    try {
      const r = await axios.patch(`${API}/jobs/${job.id}`, { status, ...extra });
      setJobs((prev) => prev.map((j) => (j.id === job.id ? r.data : j)));
    } catch (e) { notify(errText(e)); return; }
    notify(status === "Tamamlandı" ? "İş başarıyla kapatıldı" : "İş devralındı");
    if (status === "Tamamlandı") loadAdminData();
  };

  const claimJobWithGPS = async (job) => {
    // Get browser geolocation, then call claim endpoint
    const getPos = () => new Promise((res) => {
      if (!navigator.geolocation) return res(null);
      navigator.geolocation.getCurrentPosition(
        (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => res(null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });
    const pos = await getPos();
    const body = pos ? { latitude: pos.lat, longitude: pos.lng } : {};
    try {
      let r = await axios.post(`${API}/jobs/${job.id}/claim`, body);
      if (r.data.ok === false && r.data.reason === "out_of_range") {
        // Show themed confirm and wait for user answer
        const ok = await new Promise((resolve) => setOutOfRangeConfirm({ job, distance: r.data.distance_m, allowed: r.data.allowed_radius_m, resolve }));
        if (!ok) return null;
        r = await axios.post(`${API}/jobs/${job.id}/claim`, { ...body, force: true });
      }
      if (r.data.ok) {
        setJobs((prev) => prev.map((j) => (j.id === job.id ? r.data.job : j)));
        notify(pos ? `İş devralındı${r.data.distance_m != null ? ` (${Math.round(r.data.distance_m)} m)` : ""}` : "İş devralındı (GPS yok)");
        return r.data.job;
      }
    } catch (e) { notify(errText(e)); }
    return null;
  };

  const addProduct = (product) => {
    setCart((items) => {
      const exists = items.find((i) => i.id === product.id);
      return exists ? items.map((i) => (i.id === product.id ? { ...i, qty: i.qty + 1 } : i)) : [...items, { ...product, qty: 1 }];
    });
    notify(`${product.name} sepete eklendi`);
  };

  const saveInvoice = async ({ customer, taxId, address }) => {
    const payload = {
      customer, customer_tax_id: taxId, customer_address: address,
      items: cart.map((i) => ({ id: i.id, name: i.name, qty: i.qty, price: i.price, sku: i.sku })),
      subtotal, tax, total, status: "Taslak",
    };
    // Only decrement stock if the invoice has actual product line items (from cart)
    if (cart.length > 0) {
      try { await axios.post(`${API}/sales`, { items: payload.items, total, customer }); } catch (e) { console.error(e); }
    }
    try {
      const r = await axios.post(`${API}/invoices`, payload);
      setInvoices((prev) => [r.data, ...prev]);
    } catch (e) { notify(errText(e)); return; }
    setCart([]); load(); loadAdminData(); setShowInvoice(false);
    notify("Satış kaydedildi ve fatura oluşturuldu");
  };

  const openInvoiceModal = () => {
    if (cart.length === 0) {
      notify("Önce Stok & Satış sayfasından ürün ekleyin");
      setSection("stock");
      return;
    }
    setShowInvoice(true);
  };

  const nav = [
    { id: "overview", label: "Genel Bakış", icon: LayoutDashboard, roles: ["admin", "staff"] },
    { id: "jobs", label: "İş Planı", icon: BriefcaseBusiness, roles: ["admin", "staff"] },
    { id: "stock", label: "Stok & Satış", icon: Package, roles: ["admin", "staff"] },
    { id: "payments", label: "Ödeme Al", icon: CreditCard, roles: ["admin", "staff"] },
    { id: "installments", label: "Taksitli Ödeme", icon: CreditCard, roles: ["admin", "staff"] },
    { id: "invoices", label: "Faturalar", icon: FileText, roles: ["admin"] },
    { id: "collections", label: "Alınan Ödemeler", icon: Banknote, roles: ["admin"] },
    { id: "finance", label: "Gelir / Gider", icon: Wallet, roles: ["admin"] },
    { id: "reports", label: "Raporlar", icon: TrendingUp, roles: ["admin"] },
    { id: "team", label: "Personel", icon: Users, roles: ["admin"] },
  ].filter((n) => n.roles.includes(user.role) && (!Array.isArray(user.allowed_pages) || user.allowed_pages.includes(n.id)));

  return (
    <div className="shell" data-testid="app-shell">
      {mobileNav && <div className="mobile-backdrop" data-testid="mobile-backdrop" onClick={() => setMobileNav(false)} />}
      <aside className={mobileNav ? "sidebar mobile-open" : "sidebar"}>
        <div className="brand">
          <div className="brand-mark">L</div>
          <div><strong>Lokalnet<span>BT</span></strong><small>Saha operasyonu</small></div>
        </div>
        <nav>
          {nav.map(({ id, label, icon: Icon }) => (
            <button
              data-testid={`nav-${id}`}
              className={section === id ? "nav-item active" : "nav-item"}
              onClick={() => { setSection(id); setMobileNav(false); }}
              key={id}
            >
              <Icon size={18} />{label}
              {id === "jobs" && <b>{jobs.filter((j) => j.status !== "Tamamlandı").length}</b>}
            </button>
          ))}
        </nav>
        <div className="side-note">
          <div className="pulse"></div>
          <div><strong>Sistem aktif</strong><small>Son senkronizasyon şimdi</small></div>
        </div>
        <div className="user-mini" data-testid="sidebar-user-mini">
          <button className="user-mini-info" data-testid="open-profile-button" onClick={() => setShowProfile(true)} title="Profili düzenle">
            <div className="avatar">{initials(user.name)}</div>
            <div><strong>{user.name}</strong><small>{user.role === "admin" ? "Yönetici" : "Saha personeli"}</small></div>
          </button>
          <button className="logout-btn" data-testid="logout-button" onClick={logout} title="Çıkış yap"><LogOut size={15} /></button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="mobile-menu" data-testid="mobile-menu-button" onClick={() => setMobileNav(!mobileNav)}><Menu size={20} /></button>
          <div className="search">
            <Search size={17} />
            <input data-testid="global-search-input" placeholder="İş, personel veya ürün ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="top-actions">
            <NotificationsBell />
            <button className="quick-scan" data-testid="quick-scan-button" onClick={() => setShowScanner(true)}><ScanLine size={17} /> Barkod okut</button>
          </div>
        </header>

        <div className="content">
          {section === "overview" && <Overview user={user} jobs={jobs} products={products} onJobs={() => setSection("jobs")} onStock={() => setSection("stock")} onComplete={changeStatus} onClaim={claimJobWithGPS} report={report} setJobs={setJobs} notify={notify} loadAdminData={loadAdminData} />}
          {section === "jobs" && <Jobs user={user} jobs={jobs} search={search} onStatus={changeStatus} onClaim={claimJobWithGPS} onNew={() => setShowJob(true)} onEdit={setEditJob} notify={notify} setJobs={setJobs} loadAdminData={loadAdminData} onTrack={setTrackJob} />}
          {section === "reports" && <Reports report={report} profitReport={profitReport} onRefresh={loadAdminData} notify={notify} />}
          {section === "invoices" && <Invoices invoices={invoices} onNew={openInvoiceModal} onReload={load} notify={notify} isAdmin={user.role === "admin"} />}
          {section === "payments" && <StaffPayments user={user} jobs={jobs} payments={payments} onReload={load} notify={notify} />}
          {section === "collections" && <Collections payments={payments} onReload={load} />}
          {section === "finance" && <Finance data={financeData} onReload={loadAdminData} notify={notify} />}
          {section === "stock" && user.role === "admin" && (
            <Stock products={products} search={search} cart={cart} addProduct={addProduct} total={total} subtotal={subtotal} tax={tax} setCart={setCart} onInvoice={() => setShowInvoice(true)} onScan={() => setShowScanner(true)} isAdmin={true} onReload={load} notify={notify} />
          )}
          {section === "stock" && user.role !== "admin" && (
            <Stock products={products} search={search} cart={cart} addProduct={addProduct} total={total} subtotal={subtotal} tax={tax} setCart={setCart} onInvoice={() => setShowInvoice(true)} onScan={() => setShowScanner(true)} isAdmin={false} onReload={load} notify={notify} />
          )}
          {section === "team" && <Team users={staffUsers} onReload={loadAdminData} notify={notify} currentUser={user} />}
          {section === "installments" && <Installments />}
        </div>
      </main>

      {toast && <div className="toast" data-testid="toast-message"><Check size={17} />{toast}</div>}
      {showScanner && <Scanner products={products} onClose={() => setShowScanner(false)} onAdd={addProduct} onReload={load} isAdmin={user.role === "admin"} />}
      {showJob && (
        <JobModal
          staffList={staffUsers.filter((s) => s.role === "staff")}
          currentUser={user}
          onClose={() => setShowJob(false)}
          onSave={async (j) => {
            try {
              const r = await axios.post(`${API}/jobs`, j);
              setJobs((prev) => [r.data, ...prev]);
              setShowJob(false); notify("Yeni iş plana eklendi"); loadAdminData();
            } catch (e) { notify(errText(e)); }
          }}
        />
      )}
      {editJob && (
        <JobModal
          initial={editJob}
          staffList={staffUsers.filter((s) => s.role === "staff")}
          currentUser={user}
          onClose={() => setEditJob(null)}
          onSave={async (j) => {
            try {
              const r = await axios.patch(`${API}/jobs/${editJob.id}`, j);
              setJobs((prev) => prev.map((x) => (x.id === editJob.id ? r.data : x)));
              setEditJob(null); notify("İş planı güncellendi"); loadAdminData();
            } catch (e) { notify(errText(e)); }
          }}
        />
      )}
      {showInvoice && <InvoiceModal subtotal={subtotal} tax={tax} total={total} onClose={() => setShowInvoice(false)} onSave={saveInvoice} />}
      {trackJob && <TrackModal job={trackJob} onClose={() => setTrackJob(null)} />}
      {showProfile && <ProfileModal user={user} onClose={() => setShowProfile(false)} onSave={updateProfile} onLogout={logout} notify={notify} />}
      <ConfirmModal
        open={!!outOfRangeConfirm}
        title="İş adresinden uzaktasınız"
        message={outOfRangeConfirm ? `İş konumuna yaklaşık ${Math.round(outOfRangeConfirm.distance)} m uzaklıkta olduğunuz görünüyor (izin verilen ${Math.round(outOfRangeConfirm.allowed)} m). Yine de devralmak ister misiniz?` : ""}
        confirmLabel="Yine de devral"
        cancelLabel="Vazgeç"
        icon={MapPin}
        onConfirm={() => { outOfRangeConfirm.resolve(true); setOutOfRangeConfirm(null); }}
        onCancel={() => { outOfRangeConfirm && outOfRangeConfirm.resolve(false); setOutOfRangeConfirm(null); }}
      />
    </div>
  );
}

// ============ Page Head ============
function PageHead({ eyebrow, title, sub, action }) {
  return (
    <div className="page-head">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{sub}</p>
      </div>
      {action}
    </div>
  );
}

// ============ Overview ============
function Overview({ user, jobs, products, onJobs, onStock, onComplete, onClaim, report, setJobs, notify, loadAdminData }) {
  const [detail, setDetail] = useState(null);
  const active = jobs.filter((j) => j.status !== "Tamamlandı");
  const today = new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const thisMonth = new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  const revenue = report?.totals?.total_revenue ?? 0;
  const monthCompleted = report?.totals?.jobs_completed_this_month ?? 0;
  const lowStock = products.filter((p) => p.stock <= (p.low_stock || 5));
  const now = Date.now();
  const overdueJobs = user.role === "admin"
    ? jobs.filter((j) => j.status !== "Tamamlandı" && (now - new Date(j.created_at).getTime()) > 48 * 3600 * 1000)
    : [];
  const companyCounts = jobs.reduce((acc, j) => {
    const c = j.company || "Lokalnet";
    acc[c] = acc[c] || { total: 0, active: 0, done: 0 };
    acc[c].total += 1;
    if (j.status === "Tamamlandı") acc[c].done += 1; else acc[c].active += 1;
    return acc;
  }, {});
  return (
    <>
      <PageHead
        eyebrow={today}
        title={greetingFor(user.name)}
        sub={user.role === "admin" ? "Bugünün operasyonu kontrol altında. Ekibinizin son durumuna göz atın." : "Bugün size atanan işleri buradan takip edebilirsiniz."}
        action={user.role === "admin" ? <button data-testid="overview-new-job-button" className="primary" onClick={onJobs}><Plus size={18} /> Yeni iş planla</button> : null}
      />
      {overdueJobs.length > 0 && (
        <div data-testid="sla-alert-banner" style={{ margin: "0 0 14px", padding: "12px 16px", borderRadius: 12, background: "#fee2e2", border: "1px solid #fca5a5", display: "flex", alignItems: "center", gap: 10, color: "#991b1b" }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>{overdueJobs.length} iş emri 48 saati geçti ve hâlâ açık.</strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              {overdueJobs.slice(0, 3).map((j) => j.title).join(" · ")}{overdueJobs.length > 3 ? ` +${overdueJobs.length - 3} daha` : ""}
            </div>
          </div>
          <button className="text-btn" style={{ color: "#991b1c" }} onClick={onJobs} data-testid="sla-alert-goto">İş planına git <ChevronRight size={15} /></button>
        </div>
      )}
      {user.role === "admin" && (
        <div data-testid="company-stats" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {["Lokalnet", "Gibirnet"].map((c) => {
            const s = companyCounts[c] || { total: 0, active: 0, done: 0 };
            const isL = c === "Lokalnet";
            return (
              <div key={c} data-testid={`company-stat-${c.toLowerCase()}`} style={{ flex: "1 1 200px", padding: "12px 16px", borderRadius: 14, background: isL ? "#e6f0ff" : "#f3ecff", border: "1px solid " + (isL ? "#a8c8f0" : "#d9bfff") }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: isL ? "#1e40af" : "#7c3aed" }}>{c}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 4 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: "#0d1b3f" }}>{s.total}</span>
                  <span style={{ fontSize: 12, color: "#6a7080" }}>toplam iş</span>
                </div>
                <div style={{ fontSize: 12, color: "#6a7080", marginTop: 2 }}>{s.active} aktif · {s.done} tamamlandı</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="metric-grid">
        <Metric label={user.role === "admin" ? "Bugünkü işler" : "Bana atanan"} value={active.length} detail={`${jobs.length - active.length} tamamlandı`} color="blue" />
        <Metric label={`Bu ay tamamlanan (${thisMonth})`} value={monthCompleted} detail={user.role === "admin" ? "Ekipçe" : "Toplam"} color="green" />
        <Metric label="Kritik stok" value={lowStock.length} detail={lowStock.length ? "Sipariş gerekli" : "Stoklar iyi"} color="orange" />
        <Metric label="Ciro" value={money(revenue)} detail={user.role === "admin" ? "Toplam gerçekleşen" : "Panelde görünmez"} color="navy" />
        <Metric label="Personel" value={report?.totals?.staff_count ?? "-"} detail="Aktif ekip" color="navy" />
      </div>
      <div className="section-row">
        <section className="panel schedule">
          <div className="panel-head">
            <div><h2>Bugünün iş akışı</h2><p>Personellerin canlı çalışma durumu</p></div>
            <button data-testid="view-all-jobs-button" className="text-btn" onClick={onJobs}>Tümünü gör <ChevronRight size={16} /></button>
          </div>
          <div className="job-list">
            {active.length ? active.slice(0, 4).map((j) => <JobRow key={j.id} job={j} onStatus={onComplete} onOpen={setDetail} />) : <div className="empty-inline">Bugün için aktif iş yok.</div>}
          </div>
        </section>
        <section className="panel stock-summary">
          <div className="panel-head">
            <div><h2>Stok özeti</h2><p>Depo hareketleri</p></div>
            <button data-testid="view-all-stock-button" className="text-btn" onClick={onStock}>Stoka git <ChevronRight size={16} /></button>
          </div>
          <div className="stock-callout">
            <div className="stock-icon"><Package size={20} /></div>
            <div><strong>{lowStock.length} ürün kritik seviyede</strong><span>Sipariş vermeyi unutmayın</span></div>
          </div>
          {products.slice(0, 4).map((p) => (
            <div className="stock-line" key={p.id}>
              <span>{p.name}</span>
              <strong className={p.stock <= (p.low_stock || 5) ? "danger-text" : ""}>{p.stock} {p.unit || "adet"}</strong>
            </div>
          ))}
        </section>
      </div>
      {detail && (
        <JobDetail
          user={user}
          job={detail}
          onClose={() => setDetail(null)}
          onCompleted={(updated) => {
            if (setJobs) setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
            setDetail(null);
            if (notify) notify("İş başarıyla tamamlandı");
            if (loadAdminData) loadAdminData();
          }}
          onClaim={onClaim ? async () => { const updated = await onClaim(detail); if (updated) setDetail(updated); } : undefined}
        />
      )}
    </>
  );
}

function Metric({ label, value, detail, color }) {
  return (
    <div className="metric" data-testid={`metric-${label}`}>
      <div className={`metric-dot ${color}`}></div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function StaffCell({ job }) {
  const names = (job.staff_names && job.staff_names.length ? job.staff_names : [job.staff]).filter(Boolean);
  const claimed = job.claimed_by_names || [];
  if (names.length === 0) return <span className="muted">—</span>;
  if (names.length === 1) {
    return (
      <div className="person-cell">
        <div className="avatar small">{initials(names[0])}</div>
        <div className="person-info">
          <span>{names[0]}</span>
          {claimed.includes(names[0]) && <em className="claim-tag"><Check size={10} /> devraldı</em>}
        </div>
      </div>
    );
  }
  return (
    <div className="person-cell multi" title={names.join(", ")}>
      <div className="avatar-stack">
        {names.slice(0, 3).map((n, i) => {
          const isClaimer = claimed.includes(n);
          return (
            <div key={i} className={"avatar small stack" + (isClaimer ? " claimed" : "")} style={{ zIndex: 10 - i }} title={n + (isClaimer ? " (devraldı)" : "")}>
              {initials(n)}
            </div>
          );
        })}
      </div>
      <div className="person-info">
        <span>{names.length} personel</span>
        {claimed.length > 0 && (
          <em className="claim-tag" title={claimed.join(", ") + " devraldı"}>
            <Check size={10} /> {claimed.length === 1 ? `${claimed[0].split(" ")[0]} devraldı` : `${claimed.length} kişi devraldı`}
          </em>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, onStatus, onOpen }) {
  return (
    <div className="job-row" data-testid={`job-row-${job.id}`} onClick={() => onOpen && onOpen(job)} style={{ cursor: onOpen ? "pointer" : "default" }}>
      <div className="time">{job.scheduled_at}</div>
      <div className={`status-mark ${job.status === "Devam ediyor" ? "working" : "waiting"}`}></div>
      <div className="job-info"><strong>{job.title}</strong><span>{job.customer} · <MapPin size={12} />{job.address}</span></div>
      <div className="job-person"><StaffCell job={job} /></div>
      <div className="job-state">
        <span className={job.status === "Devam ediyor" ? "tag working-tag" : "tag waiting-tag"}>{job.status}</span>
      </div>
    </div>
  );
}

// Company badge
function CompanyTag({ company }) {
  const c = company || "Lokalnet";
  const isLokalnet = c === "Lokalnet";
  return (
    <span
      data-testid="company-tag"
      style={{
        display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 999,
        fontSize: 10, fontWeight: 700, letterSpacing: ".04em",
        background: isLokalnet ? "#e6f0ff" : "#f3ecff",
        color: isLokalnet ? "#1e40af" : "#7c3aed",
        border: "1px solid " + (isLokalnet ? "#a8c8f0" : "#d9bfff"),
      }}
    >{c}</span>
  );
}

// ============ Jobs ============
function Jobs({ user, jobs, search, onStatus, onClaim, onNew, onEdit, notify, setJobs, loadAdminData, onTrack }) {
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState("all"); // all | active | done
  const filtered = jobs
    .filter((j) => `${j.title} ${j.customer} ${j.staff} ${j.company || ""}`.toLowerCase().includes(search.toLowerCase()))
    .filter((j) => {
      // Saha personeli tamamlanan işleri görmez
      if (user.role !== "admin" && j.status === "Tamamlandı") return false;
      if (filter === "active") return j.status !== "Tamamlandı";
      if (filter === "done") return j.status === "Tamamlandı";
      return true;
    })
    // Yönetici: bekleyen/devam eden üstte, tamamlananlar altta; ardından planlanan saat
    .sort((a, b) => {
      const da = a.status === "Tamamlandı" ? 1 : 0;
      const db = b.status === "Tamamlandı" ? 1 : 0;
      if (da !== db) return da - db;
      return String(a.scheduled_at || "").localeCompare(String(b.scheduled_at || ""));
    });
  const askDelete = (e, job) => {
    e.stopPropagation();
    setConfirmDelete(job);
  };
  const doDelete = async () => {
    const job = confirmDelete;
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/jobs/${job.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      notify("İş silindi");
      loadAdminData();
    } catch (err) { notify(errText(err)); }
  };
  const now = Date.now();
  const isOverdue = (j) => j.status !== "Tamamlandı" && (now - new Date(j.created_at).getTime()) > 48 * 3600 * 1000;
  return (
    <>
      <PageHead
        eyebrow="Operasyon merkezi"
        title="İş planı"
        sub={user.role === "admin" ? "Ekibin tüm saha görevlerini planlayın, devredin ve takip edin." : "Size atanan işlerin listesi."}
        action={user.role === "admin" ? <button data-testid="new-job-button" className="primary" onClick={onNew}><Plus size={18} /> Yeni iş ekle</button> : null}
      />
      <div className="filter-row">
        <button className={filter === "all" ? "filter active" : "filter"} data-testid="jobs-all-filter" onClick={() => setFilter("all")}>Tümü <b>{filtered.length}</b></button>
        <button className={filter === "active" ? "filter active" : "filter"} data-testid="jobs-active-filter" onClick={() => setFilter("active")}>Aktif <b>{jobs.filter((j) => j.status !== "Tamamlandı").length}</b></button>
        <button className={filter === "done" ? "filter active" : "filter"} data-testid="jobs-completed-filter" onClick={() => setFilter("done")}>Tamamlanan <b>{jobs.filter((j) => j.status === "Tamamlandı").length}</b></button>
      </div>
      <section className="panel jobs-panel">
        <div className={"table-head" + (user.role === "admin" ? " jobs-row-with-delete" : "")}>
          <span>Saat / görev</span><span>Müşteri & konum</span><span>Personel</span><span>Durum</span><span>Ücret</span>
          {user.role === "admin" && <span></span>}
        </div>
        {filtered.length ? filtered.map((j) => (
          <div
            className={"table-row" + (user.role === "admin" ? " jobs-row-with-delete" : "")}
            key={j.id} data-testid={`job-row-${j.id}`} onClick={() => setDetail(j)}
            style={isOverdue(j) ? { borderLeft: "3px solid #dc2626", background: "#fef2f2" } : undefined}
          >
            <div>
              <strong>{j.scheduled_at}</strong>
              <span>{j.title}</span>
              <div style={{ marginTop: 3 }}><CompanyTag company={j.company} /></div>
            </div>
            <div><strong>{j.customer}</strong><span className="muted"><MapPin size={12} />{j.address}</span></div>
            <div className="person-cell"><StaffCell job={j} /></div>
            <div>
              <span className={`tag ${j.status === "Devam ediyor" ? "working-tag" : j.status === "Tamamlandı" ? "done-tag" : "waiting-tag"}`}>{j.status}</span>
              {isOverdue(j) && (
                <span data-testid={`job-${j.id}-sla-alert`} className="tag" style={{ background: "#fee2e2", color: "#b91c1c", marginLeft: 6 }} title="48 saati geçti">⏱ +48s</span>
              )}
              {(() => {
                const assigned = (j.staff_ids || []).includes(user.id) || j.staff_id === user.id || j.staff === user.name;
                const alreadyClaimed = (j.claimed_by_ids || []).includes(user.id);
                const canClaim = j.status !== "Tamamlandı" && (user.role === "admin" || assigned) && !alreadyClaimed;
                return canClaim ? (
                  <button data-testid={`job-${j.id}-claim-button`} className="claim-btn" onClick={(e) => { e.stopPropagation(); onClaim(j); }}>Devral</button>
                ) : null;
              })()}
              {j.status === "Devam ediyor" && user.role === "admin" && (
                <button data-testid={`job-${j.id}-track-button`} className="claim-btn track" onClick={(e) => { e.stopPropagation(); onTrack(j); }}>
                  <Radio size={11} /> Canlı takip
                </button>
              )}
            </div>
            <strong>{money(j.price)}</strong>
            {user.role === "admin" && (
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  data-testid={`job-${j.id}-edit-button`}
                  className="icon-btn small-edit"
                  onClick={(e) => { e.stopPropagation(); onEdit(j); }}
                  title="İşi düzenle"
                  aria-label={`${j.title} işini düzenle`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`job-${j.id}-delete-button`}
                  className="icon-danger row-delete"
                  onClick={(e) => askDelete(e, j)}
                  title="İşi sil"
                  aria-label={`${j.title} işini sil`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        )) : <div className="empty-state"><ClipboardList size={30} /><strong>İş bulunamadı</strong><span>Filtre kriterlerine uygun iş yok.</span></div>}
      </section>
      {detail && (
        <JobDetail
          user={user}
          job={detail}
          onClose={() => setDetail(null)}
          onCompleted={(updated) => {
            setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
            setDetail(null);
            notify("İş başarıyla tamamlandı");
            loadAdminData();
          }}
          onClaim={async () => { const updated = await onClaim(detail); if (updated) setDetail(updated); }}
        />
      )}
      <ConfirmModal
        open={!!confirmDelete}
        title="İşi silmek istiyor musunuz?"
        message={confirmDelete ? `"${confirmDelete.title}" işi kalıcı olarak silinecek. Bu işlem geri alınamaz.` : ""}
        confirmLabel="Evet, sil"
        cancelLabel="Vazgeç"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}

// ============ Job Detail Modal (with photo upload + complete) ============
function JobDetail({ user, job, onClose, onCompleted, onClaim }) {
  const [price, setPrice] = useState(job.price || 0);
  const [notes, setNotes] = useState(job.notes || "");
  const [photoPath, setPhotoPath] = useState(job.photo_path || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(r.data.path);
    } catch (e) { alert(errText(e)); }
    finally { setUploading(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/jobs/${job.id}`, { status: "Tamamlandı", price: Number(price), notes, photo_path: photoPath });
      onCompleted(r.data);
    } catch (e) { alert(errText(e)); }
    finally { setSaving(false); }
  };

  const photoUrl = photoPath ? `${API}/files/${photoPath}` : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">İş detayı</span><h2>{job.title}</h2></div>
          <button className="modal-close" data-testid="job-detail-close" onClick={onClose}><X /></button>
        </div>
        <div className="detail-info">
          <div><span>Müşteri</span><strong>{job.customer}</strong></div>
          <div><span>Adres</span><strong>{job.address}</strong></div>
          <div><span>Saat</span><strong>{job.scheduled_at}</strong></div>
          <div><span>Personel</span><strong>{job.staff}</strong></div>
          <div><span>Durum</span><strong>{job.status}</strong></div>
          <div><span>Şirket</span><strong><CompanyTag company={job.company} /></strong></div>
        </div>

        {/* Personel görünümü: iletişim ve adres kartı — net ve okunaklı */}
        <div data-testid="job-detail-contact" style={{ margin: "4px 0 12px", padding: 14, borderRadius: 12, background: "#f6f8ff", border: "1px solid #dfe7fb", display: "grid", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <MapPin size={18} color="#1e40af" style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 11, color: "#6a7080", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 700 }}>Açık adres</div>
              <div style={{ fontWeight: 600, color: "#0d1b3f" }}>{job.address || "Adres girilmemiş"}</div>
            </div>
          </div>
          {job.customer_phone ? (
            <a href={`tel:${job.customer_phone}`} data-testid="job-detail-customer-phone" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#0d9f4a", color: "#fff", textDecoration: "none", fontWeight: 700 }}>
              <Phone size={18} />
              <div>
                <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: ".08em" }}>Müşteriyi ara</div>
                <div>{job.customer_phone}</div>
              </div>
            </a>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#fff8e6", border: "1px solid #f3d38a", color: "#7a5a00", fontSize: 13 }}>
              <Phone size={16} /> Müşteri telefonu kayıtlı değil
            </div>
          )}
        </div>

        {(() => {
          const assigned = user && ((job.staff_ids || []).includes(user.id) || job.staff_id === user.id || job.staff === user.name);
          const alreadyClaimed = user && (job.claimed_by_ids || []).includes(user.id);
          const canClaim = onClaim && job.status !== "Tamamlandı" && (user?.role === "admin" || assigned) && !alreadyClaimed;
          return canClaim ? (
            <button className="primary wide" data-testid="job-detail-claim" onClick={onClaim}>
              <Check size={17} /> İşi devral
            </button>
          ) : null;
        })()}

        {job.status !== "Tamamlandı" && (
          <>
            <div className="detail-form">
              <label>Ücret<input data-testid="job-detail-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} /></label>
              <label>Notlar<textarea data-testid="job-detail-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" placeholder="Yapılan işlem, malzeme, müşteri notu..." /></label>
              <div className="photo-block">
                <span className="photo-label">Tamamlama fotoğrafı</span>
                {photoUrl ? (
                  <div className="photo-preview">
                    <AuthImage path={photoPath} alt="İş fotoğrafı" />
                    <button className="outline-btn" onClick={() => fileRef.current?.click()}><Camera size={15} /> Değiştir</button>
                  </div>
                ) : (
                  <button className="photo-drop" data-testid="job-photo-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? <span>Yükleniyor...</span> : <><ImageIcon size={22} /><span>Fotoğraf çek / yükle</span><small>Kamera veya galeri</small></>}
                  </button>
                )}
                <input
                  ref={fileRef}
                  data-testid="job-photo-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                />
              </div>
            </div>
            <button className="primary wide" data-testid="job-detail-complete" onClick={complete} disabled={saving || uploading}>
              <Check size={17} /> {saving ? "Kapatılıyor..." : "İşi tamamla"}
            </button>
          </>
        )}

        {job.status === "Tamamlandı" && (
          <>
            <div className="detail-info">
              <div><span>Ücret</span><strong>{money(job.price)}</strong></div>
              {job.notes && <div><span>Notlar</span><strong style={{ fontWeight: 400 }}>{job.notes}</strong></div>}
            </div>
            {photoUrl && <div className="photo-preview large"><AuthImage path={photoPath} alt="Tamamlama fotoğrafı" /></div>}
          </>
        )}
      </div>
    </div>
  );
}

// ============ Auth Image (fetch as blob) ============
function AuthImage({ path, alt }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const r = await axios.get(`${API}/files/${path}`, { responseType: "blob" });
        const b = URL.createObjectURL(r.data);
        setUrl(b); revoke = b;
      } catch {}
    })();
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [path]);
  return url ? <img src={url} alt={alt} /> : <div className="img-loading">Yükleniyor...</div>;
}

// ============ Stock ============
function Stock({ products, search, cart, addProduct, total, subtotal, tax, setCart, onInvoice, onScan, isAdmin, onReload, notify }) {
  const [query, setQuery] = useState(search);
  const [editing, setEditing] = useState(null);
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState(null);
  const filtered = products.filter((p) => `${p.name} ${p.sku} ${p.barcode}`.toLowerCase().includes(query.toLowerCase()));
  const doDeleteProduct = async () => {
    const p = confirmDeleteProduct;
    setConfirmDeleteProduct(null);
    try { await axios.delete(`${API}/products/${p.id}`); onReload(); notify("Ürün silindi"); }
    catch (e) { notify(errText(e)); }
  };
  return (
    <>
      <PageHead
        eyebrow="Depo & satış noktası"
        title="Stok ve satış"
        sub="Barkodla ürün bulun, stoktan düşürün ve satışınızı tamamlayın."
        action={
          <div style={{ display: "flex", gap: 8 }}>
            {isAdmin && <button data-testid="add-product-button" className="outline-btn" onClick={() => setEditing({})}><Plus size={16} /> Ürün ekle</button>}
            <button data-testid="stock-refresh-button" className="outline-btn" onClick={onReload} title="Yenile"><RefreshCw size={16} /></button>
            <button data-testid="stock-scan-button" className="secondary" onClick={onScan}><ScanLine size={18} /> Barkod okut</button>
          </div>
        }
      />
      <div className="pos-layout">
        <section>
          <div className="stock-toolbar">
            <div className="stock-search">
              <Search size={16} />
              <input data-testid="stock-search-input" placeholder="Ürün adı veya barkod..." value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
          </div>
          <div className="product-grid">
            {filtered.map((p) => (
              <div className="product-card-wrap" key={p.id}>
                <button data-testid={`product-${p.id}-card`} className="product-card" onClick={() => addProduct(p)}>
                  <div className="product-art"><Package size={28} /></div>
                  <div><strong>{p.name}</strong><small>{p.sku} · {p.stock} {p.unit || "adet"}</small></div>
                  <b>{money(p.price * 1.20)}</b>
                  <small style={{ color: "#6a7080", fontSize: 10, marginTop: -4 }}>KDV dahil · net {money(p.price)}</small>
                  {isAdmin && p.purchase_price > 0 && (
                    <div className="margin-badge">
                      <Percent size={10} /> {p.price > 0 ? Math.round(((p.price - p.purchase_price) / p.price) * 100) : 0}% kar
                    </div>
                  )}
                  {p.stock <= (p.low_stock || 5) && <em>Kritik stok</em>}
                </button>
                {isAdmin && (
                  <div className="product-actions">
                    <button data-testid={`product-${p.id}-edit`} className="product-edit-btn" onClick={() => setEditing(p)} title="Düzenle" aria-label={`${p.name} ürününü düzenle`}>
                      <Pencil size={12} />
                    </button>
                    <button data-testid={`product-${p.id}-delete`} className="product-edit-btn danger" onClick={() => setConfirmDeleteProduct(p)} title="Sil" aria-label={`${p.name} ürününü sil`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {filtered.length === 0 && <div className="empty-state" style={{ gridColumn: "1/-1" }}><Package size={30} /><strong>Ürün bulunamadı</strong><span>Arama kriterlerine uygun ürün yok.</span></div>}
          </div>
        </section>
        <aside className="cart-panel">
          <div className="cart-head">
            <div><h2>Satış sepeti</h2><span>{cart.length} ürün</span></div>
            <button data-testid="clear-cart-button" onClick={() => setCart([])}><X size={17} /></button>
          </div>
          {cart.length === 0 ? (
            <div className="empty-cart"><ShoppingCart size={30} /><strong>Sepetiniz boş</strong><span>Satış için bir ürün seçin</span></div>
          ) : (
            <div className="cart-items">
              {cart.map((i) => (
                <div className="cart-item" key={i.id}>
                  <div><strong>{i.name}</strong><small>{i.qty} × {money(i.price)}</small></div>
                  <b>{money(i.qty * i.price)}</b>
                </div>
              ))}
            </div>
          )}
          <div className="cart-bottom">
            <div><span>Ara toplam</span><strong>{money(subtotal)}</strong></div>
            <div><span>KDV (%20)</span><strong>{money(tax)}</strong></div>
            <div className="total-line"><span>Genel toplam</span><strong>{money(total)}</strong></div>
            <button data-testid="create-invoice-button" className="primary wide" disabled={!cart.length} onClick={onInvoice}>
              <FileText size={17} /> Fatura oluştur
            </button>
          </div>
        </aside>
      </div>
      {editing !== null && (
        <ProductModal
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); notify(editing.id ? "Ürün güncellendi" : "Ürün eklendi"); }}
        />
      )}
      <ConfirmModal
        open={!!confirmDeleteProduct}
        title="Ürünü silmek istiyor musunuz?"
        message={confirmDeleteProduct ? `"${confirmDeleteProduct.name}" ürünü stok listenizden kalıcı olarak kaldırılacak.` : ""}
        confirmLabel="Evet, sil"
        cancelLabel="Vazgeç"
        danger
        onConfirm={doDeleteProduct}
        onCancel={() => setConfirmDeleteProduct(null)}
      />
    </>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || "",
    sku: product.sku || "",
    barcode: product.barcode || "",
    stock: product.stock ?? 0,
    price: product.price ?? 0,
    purchase_price: product.purchase_price ?? 0,
    unit: product.unit || "adet",
    low_stock: product.low_stock ?? 5,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const margin = form.price > 0 ? Math.round(((form.price - form.purchase_price) / form.price) * 100) : 0;
  const submit = async () => {
    setSaving(true); setError("");
    const payload = { ...form, stock: Number(form.stock), price: Number(form.price), purchase_price: Number(form.purchase_price), low_stock: Number(form.low_stock) };
    try {
      if (isNew) await axios.post(`${API}/products`, payload);
      else await axios.patch(`${API}/products/${product.id}`, payload);
      onSaved();
    } catch (e) { setError(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">Ürün</span><h2>{isNew ? "Yeni ürün" : "Ürün düzenle"}</h2></div>
          <button className="modal-close" data-testid="product-modal-close" onClick={onClose}><X /></button>
        </div>
        <div className="form-grid">
          <label>Ürün adı<input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>SKU<input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></label>
          <label>Barkod<input data-testid="product-barcode-input" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></label>
          <label>Birim<input data-testid="product-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></label>
          <label>Alış fiyatı (₺)<input data-testid="product-purchase-price-input" type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></label>
          <label>Satış fiyatı (₺)<input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
          <label>Stok<input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></label>
          <label>Kritik stok eşiği<input data-testid="product-lowstock-input" type="number" value={form.low_stock} onChange={(e) => setForm({ ...form, low_stock: e.target.value })} /></label>
        </div>
        <div className="margin-preview">
          <span>Kar marjı</span>
          <strong>{money(form.price - form.purchase_price)} / birim · %{margin}</strong>
        </div>
        {error && <div className="login-error">{error}</div>}
        <button data-testid="product-save-button" className="primary wide" disabled={!form.name || !form.sku || saving} onClick={submit}>
          {saving ? "Kaydediliyor..." : (isNew ? "Ürün ekle" : "Kaydet")}
        </button>
      </div>
    </div>
  );
}

// ============ Invoices + PDF ============
function generateInvoicePDF(inv) {
  const doc = new jsPDF();
  doc.setFontSize(22);
  doc.text("LokalnetBT", 14, 22);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text("Saha Operasyon & Satış Sistemi", 14, 28);

  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text("FATURA", 196, 22, { align: "right" });
  doc.setFontSize(10);
  doc.text(`No: ${inv.number || inv.id.slice(0, 8)}`, 196, 28, { align: "right" });
  doc.text(`Tarih: ${new Date(inv.created_at).toLocaleDateString("tr-TR")}`, 196, 34, { align: "right" });
  doc.text(`Durum: ${inv.status}`, 196, 40, { align: "right" });

  doc.setDrawColor(200);
  doc.line(14, 48, 196, 48);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text("MÜŞTERI", 14, 55);
  doc.setFontSize(11);
  doc.setTextColor(0);
  doc.text(inv.customer || "-", 14, 62);
  if (inv.customer_address) { doc.setFontSize(9); doc.setTextColor(80); doc.text(inv.customer_address, 14, 68); }
  if (inv.customer_tax_id) { doc.setFontSize(9); doc.setTextColor(80); doc.text(`VKN/TCKN: ${inv.customer_tax_id}`, 14, 74); }

  autoTable(doc, {
    startY: 82,
    head: [["#", "Ürün / Hizmet", "SKU", "Miktar", "Birim Fiyat", "Tutar"]],
    body: (inv.items || []).map((i, idx) => [
      idx + 1, i.name, i.sku || "-", i.qty, money(i.price), money(i.qty * i.price),
    ]),
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { cellWidth: 10 }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
  });

  const finalY = doc.lastAutoTable.finalY + 8;
  const subtotal = inv.subtotal ?? (inv.items || []).reduce((s, i) => s + i.qty * i.price, 0);
  const tax = inv.tax ?? subtotal * 0.2;
  const total = inv.total || subtotal + tax;
  doc.setFontSize(10);
  doc.text(`Ara toplam:  ${money(subtotal)}`, 196, finalY, { align: "right" });
  doc.text(`KDV (%20):  ${money(tax)}`, 196, finalY + 6, { align: "right" });
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text(`GENEL TOPLAM:  ${money(total)}`, 196, finalY + 14, { align: "right" });

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.setFont(undefined, "normal");
  doc.text("LokalnetBT · Bu belge sistemden üretilmiştir.", 14, 285);
  if (inv.created_by) doc.text(`Düzenleyen: ${inv.created_by}`, 196, 285, { align: "right" });

  doc.save(`fatura-${inv.number || inv.id.slice(0, 8)}.pdf`);
}

function Invoices({ invoices, onNew, onReload, notify, isAdmin }) {
  const [sending, setSending] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const sendNette = async (inv) => {
    setSending(inv.id);
    try {
      const r = await axios.post(`${API}/nettefatura/send/${inv.id}`);
      const data = r.data;
      if (data.success) {
        notify("e-Fatura NetteFatura'ya gönderildi");
      } else {
        alert(`${data.message}\n\nUBL-TR XML üretildi ve indirilebilir hale getirildi.`);
      }
      onReload();
    } catch (e) { notify(errText(e)); }
    finally { setSending(null); }
  };
  const downloadXml = async (inv) => {
    try {
      const r = await axios.get(`${API}/nettefatura/xml/${inv.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `efatura-${inv.number || inv.id.slice(0, 8)}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { notify(errText(e)); }
  };
  const doDelete = async () => {
    const inv = confirmDelete;
    setConfirmDelete(null);
    try { await axios.delete(`${API}/invoices/${inv.id}`); onReload(); notify("Fatura silindi"); }
    catch (e) { notify(errText(e)); }
  };
  return (
    <>
      <PageHead
        eyebrow="Belge merkezi"
        title="Faturalar"
        sub="Hazırladığınız faturaları PDF olarak indirin veya NetteFatura'ya (UBL-TR XML) gönderin."
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="invoices-refresh-button" className="outline-btn" onClick={onReload} title="Yenile"><RefreshCw size={16} /></button>
            <button data-testid="invoice-new-button" className="primary" onClick={onNew}><Plus size={18} /> Yeni fatura</button>
          </div>
        }
      />
      <section className="panel jobs-panel">
        <div className="invoice-banner">
          <FileText size={24} />
          <div>
            <strong>NetteFatura (İşNet) — Test modu</strong>
            <span>UBL-TR XML üretimi hazır. Canlı SOAP gönderim için sunucu IP'nizin İşNet'te tanımlı olması gerekir. XML'i indirip portala manuel yükleyebilirsiniz.</span>
          </div>
          <span className="tag waiting-tag">Test</span>
        </div>
        {invoices.length ? invoices.map((i) => (
          <div className="table-row invoice-row" key={i.id}>
            <div>
              <strong>{i.number || i.id.slice(0, 8)}</strong>
              <span>{new Date(i.created_at).toLocaleDateString("tr-TR")}</span>
            </div>
            <div>
              <strong>{i.customer}</strong>
              <span className="muted">{i.customer_tax_id ? `VKN: ${i.customer_tax_id}` : "Perakende satış"}</span>
            </div>
            <div><span className={`tag ${i.nettefatura_status === "sent" ? "done-tag" : "waiting-tag"}`}>{i.status}</span></div>
            <strong>{money(i.total)}</strong>
            <div className="invoice-actions">
              <button data-testid={`invoice-${i.id}-receipt-button`} className="primary" onClick={async () => {
                try {
                  const r = await axios.get(`${API}/invoices/${i.id}/pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url; a.download = `fis-${i.number || i.id.slice(0, 8)}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) { notify(errText(e)); }
              }} title="Fiş PDF indir">
                <FileText size={13} /> Fiş
              </button>
              <button data-testid={`invoice-${i.id}-export-button`} className="outline-btn" onClick={() => generateInvoicePDF(i)} title="A4 Fatura PDF indir">
                <FileText size={13} /> A4
              </button>
              <button data-testid={`invoice-${i.id}-xml-button`} className="outline-btn" onClick={() => downloadXml(i)} title="e-Fatura XML indir">
                <Download size={13} /> XML
              </button>
              {isAdmin && (
                <button data-testid={`invoice-${i.id}-send-button`} className="secondary" onClick={() => sendNette(i)} disabled={sending === i.id} title="NetteFatura'ya gönder">
                  <Send size={13} /> {sending === i.id ? "..." : "e-Fatura"}
                </button>
              )}
              {isAdmin && (
                <button data-testid={`invoice-${i.id}-delete-button`} className="icon-danger" onClick={() => setConfirmDelete(i)} title="Faturayı sil" aria-label={`${i.number || i.id} faturasını sil`}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        )) : (
          <div className="empty-state"><FileText size={30} /><strong>Henüz fatura yok</strong><span>Satış sepetinden ilk fatura taslağınızı oluşturun.</span></div>
        )}
      </section>
      <ConfirmModal
        open={!!confirmDelete}
        title="Faturayı silmek istiyor musunuz?"
        message={confirmDelete ? `${confirmDelete.number || confirmDelete.id.slice(0, 8)} numaralı fatura kalıcı olarak silinecek.` : ""}
        confirmLabel="Evet, sil"
        cancelLabel="Vazgeç"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}

// ============ Reports ============
function Reports({ report, profitReport, onRefresh, notify }) {
  const [sendingReport, setSendingReport] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const sendMonthly = async () => {
    setSendingReport(true); setReportStatus(null);
    try {
      const r = await axios.post(`${API}/reports/monthly/send`);
      if (r.data?.ok) setReportStatus({ ok: true, msg: `${r.data.month} raporu ${r.data.to} adresine gönderildi.` });
      else setReportStatus({ ok: false, msg: r.data?.delivery?.error || r.data?.error || "Gönderilemedi" });
    } catch (e) { setReportStatus({ ok: false, msg: errText(e) }); }
    finally { setSendingReport(false); }
  };
  if (!report) return <div className="empty-state"><TrendingUp size={30} /><strong>Rapor yükleniyor...</strong></div>;
  const t = report.totals;
  const pt = profitReport?.totals;
  return (
    <>
      <PageHead
        eyebrow="Analitik"
        title="Yönetici raporları"
        sub="Ciro, kar marjı, personel performansı ve stok durumu tek ekranda."
        action={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="secondary" data-testid="report-send-monthly" onClick={sendMonthly} disabled={sendingReport}>
              <Send size={15} /> {sendingReport ? "Gönderiliyor..." : "Aylık raporu şimdi e-postala"}
            </button>
            <button className="secondary" data-testid="report-refresh-button" onClick={onRefresh}>Yenile</button>
          </div>
        }
      />
      {reportStatus && (
        <div data-testid="report-send-status" style={{ margin: "10px 0", padding: "10px 14px", borderRadius: 10, background: reportStatus.ok ? "#e6f7ec" : "#fdecec", color: reportStatus.ok ? "#0d6032" : "#8a1f1f", border: "1px solid " + (reportStatus.ok ? "#a8e0b9" : "#f3b0b0") }}>
          {reportStatus.msg}
        </div>
      )}
      <div className="metric-grid">
        <Metric label="Toplam ciro" value={money(t.total_revenue)} detail={`Satış ${money(t.sales_revenue)} · İş ${money(t.job_revenue)}`} color="green" />
        <Metric label="Net kar" value={pt ? money(pt.profit) : "-"} detail={pt ? `Marj %${pt.margin_pct}` : "Satış verisi yok"} color="blue" />
        <Metric label="Aktif iş" value={t.jobs_active} detail={`${t.jobs_completed} tamamlandı`} color="orange" />
        <Metric label="Kritik stok" value={t.low_stock_count} detail={`${t.products_count} üründen`} color="navy" />
      </div>

      {pt && (
        <section className="panel profit-panel">
          <div className="panel-head">
            <div><h2>Kar marjı özeti</h2><p>Satışlardan elde edilen kar</p></div>
          </div>
          <div className="profit-cards">
            <div className="profit-card"><span>Ciro</span><strong>{money(pt.revenue)}</strong></div>
            <div className="profit-card"><span>Maliyet</span><strong>{money(pt.cost)}</strong></div>
            <div className="profit-card highlight"><span>Net kar</span><strong>{money(pt.profit)}</strong></div>
            <div className="profit-card"><span>Marj</span><strong>%{pt.margin_pct}</strong></div>
          </div>
          {profitReport.monthly?.length > 0 && (
            <>
              <h3 className="sub-heading">Aylık trend</h3>
              <div className="month-list">
                {profitReport.monthly.map((m) => (
                  <div className="month-row" key={m.month}>
                    <span>{m.month}</span>
                    <div className="month-bars">
                      <div className="month-bar rev" style={{ width: `${pt.revenue > 0 ? (m.revenue / pt.revenue) * 100 : 0}%` }} />
                      <div className="month-bar prof" style={{ width: `${pt.revenue > 0 ? (m.profit / pt.revenue) * 100 : 0}%` }} />
                    </div>
                    <div className="month-nums">
                      <small>Ciro {money(m.revenue)}</small>
                      <strong>Kar {money(m.profit)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <section className="panel" data-testid="company-report-panel" style={{ marginBottom: 18 }}>
        <div className="panel-head">
          <div><h2>Hizmet türüne göre rapor</h2><p>Gibirnet (İnternet) ve Lokalnet (Kamera/Güvenlik) işleri ayrı istatistikler</p></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14, padding: "0 4px 6px" }}>
          {(report.by_company || []).map((c) => {
            const isL = c.company === "Lokalnet";
            return (
              <div key={c.company} data-testid={`company-report-${(c.company || "").toLowerCase()}`} style={{ borderRadius: 14, padding: "16px 18px", background: isL ? "#e6f0ff" : "#f3ecff", border: "1px solid " + (isL ? "#a8c8f0" : "#d9bfff") }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: isL ? "#1e40af" : "#7c3aed" }}>{c.company}</div>
                <div style={{ display: "flex", gap: 18, marginTop: 8, flexWrap: "wrap" }}>
                  <div><div style={{ fontSize: 22, fontWeight: 800, color: "#0d1b3f" }}>{c.total}</div><div style={{ fontSize: 11, color: "#6a7080" }}>toplam iş</div></div>
                  <div><div style={{ fontSize: 22, fontWeight: 800, color: "#0d6032" }}>{c.completed}</div><div style={{ fontSize: 11, color: "#6a7080" }}>tamamlandı</div></div>
                  <div><div style={{ fontSize: 22, fontWeight: 800, color: "#b45309" }}>{c.active}</div><div style={{ fontSize: 11, color: "#6a7080" }}>aktif</div></div>
                  <div><div style={{ fontSize: 22, fontWeight: 800, color: "#1e40af" }}>{money(c.revenue)}</div><div style={{ fontSize: 11, color: "#6a7080" }}>ciro</div></div>
                  {c.avg_close_hours != null && (
                    <div><div style={{ fontSize: 22, fontWeight: 800, color: "#7c3aed" }}>{String(c.avg_close_hours).replace(".", ",")}s</div><div style={{ fontSize: 11, color: "#6a7080" }}>ort. kapanma</div></div>
                  )}
                </div>
              </div>
            );
          })}
          {(!report.by_company || report.by_company.length === 0) && <div className="empty-inline">Henüz iş verisi yok.</div>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <div><h2>Personel performansı</h2><p>Tamamlanan iş sayısı ve ciro</p></div>
          <button
            className="secondary"
            data-testid="excel-export-button"
            onClick={async () => {
              const month = (document.querySelector('[data-testid="excel-month-input"]')?.value) || "";
              try {
                const r = await axios.get(`${API}/reports/jobs-export.xlsx${month ? `?month=${month}` : ""}`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement("a");
                a.href = url; a.download = `is-emirleri-${month || "tum"}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                if (notify) notify("Excel indirildi");
              } catch (e) { if (notify) notify(errText(e)); }
            }}
          >
            <Download size={15} /> Excel indir
          </button>
        </div>
        <div style={{ padding: "0 4px 8px", display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 12, color: "#6a7080" }}>Ay filtresi (opsiyonel):</label>
          <input type="month" data-testid="excel-month-input" defaultValue={new Date().toISOString().slice(0, 7)} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e6e8ef" }} />
        </div>
        <div className="perf-list">
          {report.staff_performance.length ? report.staff_performance.map((s) => (
            <div className="perf-row" key={s.staff} data-testid={`perf-row-${s.staff}`}>
              <div className="avatar small">{initials(s.staff)}</div>
              <div className="perf-info">
                <strong>{s.staff}</strong>
                <span>{s.done} / {s.total} iş tamamlandı</span>
              </div>
              <div className="perf-bar"><div style={{ width: `${s.total ? (s.done / s.total) * 100 : 0}%` }} /></div>
              <strong className="perf-money">{money(s.revenue)}</strong>
            </div>
          )) : <div className="empty-inline">Henüz veri yok.</div>}
        </div>
      </section>

      <div className="section-row" style={{ marginTop: 18 }}>
        <section className="panel">
          <div className="panel-head"><div><h2>En karlı ürünler</h2><p>Kar bazlı ilk 5</p></div></div>
          {profitReport?.top_profit_products?.length ? profitReport.top_profit_products.slice(0, 5).map((p) => (
            <div className="stock-line" key={p.id}>
              <div><strong>{p.name}</strong><small style={{ color: "#94a3b8" }}>{p.qty} satış · {money(p.revenue)} ciro</small></div>
              <strong style={{ color: "#059669" }}>+{money(p.profit)}</strong>
            </div>
          )) : <div className="empty-inline">Henüz satış yok.</div>}
        </section>
        <section className="panel">
          <div className="panel-head"><div><h2>Kritik stok</h2><p>Sipariş verilmesi gereken ürünler</p></div></div>
          {report.low_stock_products.length ? report.low_stock_products.map((p) => (
            <div className="stock-line" key={p.id}>
              <span>{p.name}</span>
              <strong className="danger-text">{p.stock} / eşik {p.low_stock}</strong>
            </div>
          )) : <div className="empty-inline">Tüm stoklar iyi durumda.</div>}
        </section>
      </div>
    </>
  );
}

// ============ Team (admin only) ============
const ALL_PAGES = [
  { id: "overview", label: "Genel Bakış" },
  { id: "jobs", label: "İş Planı" },
  { id: "stock", label: "Stok & Satış" },
  { id: "payments", label: "Ödeme Al" },
  { id: "installments", label: "Taksitli Ödeme" },
  { id: "invoices", label: "Faturalar" },
  { id: "collections", label: "Alınan Ödemeler" },
  { id: "finance", label: "Gelir / Gider" },
  { id: "reports", label: "Raporlar" },
  { id: "team", label: "Personel" },
];

function PermissionsModal({ user, onClose, onSaved, notify }) {
  const roleDefault = user.role === "admin" ? ALL_PAGES.map((p) => p.id) : ["overview", "jobs", "stock", "payments", "installments"];
  const [sel, setSel] = useState(Array.isArray(user.allowed_pages) ? user.allowed_pages : roleDefault);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const save = async (value) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${user.id}`, { allowed_pages: value });
      onSaved(); notify("Sayfa yetkileri güncellendi"); onClose();
    } catch (e) { notify(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">Yetkilendirme</span><h2>{user.name} — sayfa erişimi</h2></div>
          <button className="modal-close" data-testid="perm-close" onClick={onClose}><X /></button>
        </div>
        <p style={{ fontSize: 13, color: "#6a7080", margin: "0 0 12px" }}>Kullanıcının görebileceği menüleri seçin. Tüm işaretleri kaldırırsanız "Role göre varsayılan" butonu ile sıfırlayabilirsiniz.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {ALL_PAGES.map((p) => (
            <label key={p.id} data-testid={`perm-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid " + (sel.includes(p.id) ? "#a8c8f0" : "#e6e8ef"), background: sel.includes(p.id) ? "#e6f0ff" : "#fff", cursor: "pointer" }}>
              <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{p.label}</span>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button className="secondary" data-testid="perm-reset" onClick={() => save(null)} disabled={saving}>Role göre varsayılan</button>
          <button className="primary" style={{ flex: 1 }} data-testid="perm-save" disabled={saving || sel.length === 0} onClick={() => save(sel)}>
            {saving ? "Kaydediliyor..." : `Kaydet (${sel.length} sayfa)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Team({ users, onReload, notify, currentUser }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permUser, setPermUser] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const doRemove = async () => {
    const u = confirmRemove;
    setConfirmRemove(null);
    try { await axios.delete(`${API}/users/${u.id}`); onReload(); notify("Kullanıcı silindi"); }
    catch (e) { notify(errText(e)); }
  };
  return (
    <>
      <PageHead
        eyebrow="Ekip yönetimi"
        title="Personel hesapları"
        sub="Yönetici ve saha personellerini buradan ekleyip yönetin — telefon numarası varsa SMS otomatik gider."
        action={<button className="primary" data-testid="team-add-button" onClick={() => setShow(true)}><UserPlus size={18} /> Yeni personel</button>}
      />
      <section className="panel jobs-panel">
        <div className="table-head team-head">
          <span>Ad Soyad</span><span>E-posta</span><span>Telefon</span><span>Rol</span><span></span>
        </div>
        {users.map((u) => (
          <div className="table-row team-row" key={u.id} data-testid={`team-row-${u.id}`}>
            <div className="person-cell"><div className="avatar small">{initials(u.name)}</div>{u.name}</div>
            <div>{u.email}</div>
            <div>
              {u.phone ? (
                <span className="phone-cell" data-testid={`team-phone-${u.id}`}>{u.phone}</span>
              ) : (
                <span className="phone-missing" data-testid={`team-phone-missing-${u.id}`}>Telefon yok</span>
              )}
            </div>
            <div><span className={`tag ${u.role === "admin" ? "done-tag" : "working-tag"}`}>{u.role === "admin" ? "Yönetici" : "Saha personeli"}</span></div>
            <div className="team-actions">
              <button className="icon-btn small-edit" data-testid={`team-perm-${u.id}`} onClick={() => setPermUser(u)} title="Sayfa yetkileri" aria-label={`${u.name} sayfa yetkileri`}><Users size={14} /></button>
              <button className="icon-btn small-edit" data-testid={`team-edit-${u.id}`} onClick={() => setEditing(u)} title="Düzenle" aria-label={`${u.name} kullanıcısını düzenle`}><Pencil size={14} /></button>
              {u.phone && (
                <button className="icon-btn small-edit" data-testid={`team-testsms-${u.id}`} onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/notifications/test-sms`, { phone: u.phone, message: `LokalnetBT test SMS · ${u.name}` });
                    const ok = r.data?.results?.[0]?.result?.ok;
                    notify(ok ? `Test SMS ${u.phone} numarasına gönderildi.` : `SMS gitmedi: ${JSON.stringify(r.data?.results?.[0]?.result || {}).slice(0, 200)}`);
                  } catch (e) { notify(errText(e)); }
                }} title={`${u.phone} numarasına test SMS gönder`} aria-label="Test SMS"><Send size={14} /></button>
              )}
              {u.id !== currentUser.id && (
                <button className="icon-danger" data-testid={`team-delete-${u.id}`} onClick={() => setConfirmRemove(u)} title="Sil" aria-label={`${u.name} kullanıcısını sil`}><Trash2 size={15} /></button>
              )}
            </div>
          </div>
        ))}
      </section>
      {show && <UserModal onClose={() => setShow(false)} onSaved={() => { setShow(false); onReload(); notify("Personel eklendi"); }} />}
      {editing && (
        <UserModal
          editUser={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); notify("Personel güncellendi"); }}
        />
      )}
      {permUser && (
        <PermissionsModal user={permUser} onClose={() => setPermUser(null)} onSaved={onReload} notify={notify} />
      )}
      <ConfirmModal
        open={!!confirmRemove}
        title="Kullanıcıyı silmek istiyor musunuz?"
        message={confirmRemove ? `${confirmRemove.name} (${confirmRemove.email}) hesabı kalıcı olarak silinecek.` : ""}
        confirmLabel="Evet, sil"
        cancelLabel="Vazgeç"
        danger
        onConfirm={doRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </>
  );
}

function ProfileModal({ user, onClose, onSave, onLogout, notify }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const saveProfile = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {};
      if (name !== user.name) payload.name = name;
      if (phone !== (user.phone || "")) payload.phone = phone;
      if (newPassword) { payload.password = newPassword; payload.current_password = currentPassword; }
      if (Object.keys(payload).length === 0) { setError("Değişiklik yok"); setSaving(false); return; }
      await onSave(payload);
      notify("Profil güncellendi");
      onClose();
    } catch (e) { setError(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal small-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">Hesabım</span><h2>Profil</h2></div>
          <button className="modal-close" data-testid="profile-modal-close" onClick={onClose}><X /></button>
        </div>
        <div className="profile-header">
          <div className="avatar big">{initials(name || user.name)}</div>
          <div>
            <strong>{user.email}</strong>
            <small>{user.role === "admin" ? "Yönetici" : "Saha personeli"}</small>
          </div>
        </div>
        <label>Ad Soyad
          <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Adınız Soyadınız" />
        </label>
        <label>Telefon (SMS/WhatsApp bildirimleri için)
          <input data-testid="profile-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0532 000 00 00" />
        </label>
        <div className="profile-divider"><span>Şifre değiştir (opsiyonel)</span></div>
        <label>Mevcut şifre
          <input data-testid="profile-current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
        </label>
        <label>Yeni şifre
          <input data-testid="profile-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="En az 6 karakter" />
        </label>
        {error && <div className="login-error">{error}</div>}
        <div className="profile-actions">
          <button className="primary wide" data-testid="profile-save-button" disabled={saving} onClick={saveProfile}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
          <button className="danger-btn wide" data-testid="profile-logout-button" onClick={() => { onClose(); onLogout(); }}>
            <LogOut size={15} /> Çıkış yap
          </button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ onClose, onSaved, editUser }) {
  const isEdit = !!editUser;
  const [form, setForm] = useState(
    isEdit
      ? { name: editUser.name || "", email: editUser.email || "", password: "", role: editUser.role || "staff", phone: editUser.phone || "" }
      : { name: "", email: "", password: "", role: "staff", phone: "" }
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setError(""); setSaving(true);
    try {
      if (isEdit) {
        const payload = {};
        if (form.name !== editUser.name) payload.name = form.name;
        if (form.email !== editUser.email) payload.email = form.email;
        if (form.role !== editUser.role) payload.role = form.role;
        if ((form.phone || "") !== (editUser.phone || "")) payload.phone = form.phone;
        if (form.password) payload.password = form.password;
        if (Object.keys(payload).length === 0) {
          setError("Değişiklik yok"); setSaving(false); return;
        }
        await axios.patch(`${API}/users/${editUser.id}`, payload);
      } else {
        await axios.post(`${API}/users`, form);
      }
      onSaved();
    }
    catch (e) { setError(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">{isEdit ? "Düzenle" : "Yeni kayıt"}</span><h2>{isEdit ? `${editUser.name} — düzenle` : "Personel ekle"}</h2></div>
          <button className="modal-close" data-testid="user-modal-close" onClick={onClose}><X /></button>
        </div>
        <div className="form-grid">
          <label>Ad Soyad<input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn. Ahmet Yılmaz" /></label>
          <label>E-posta<input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ornek@lokalnetbt.com" /></label>
          <label>Telefon (SMS için)<input data-testid="user-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0532 000 00 00" /></label>
          <label>{isEdit ? "Yeni şifre (isteğe bağlı)" : "Şifre"}<input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={isEdit ? "Değiştirmek için doldurun" : "En az 6 karakter"} /></label>
          <label>Rol<select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="staff">Saha personeli</option>
            <option value="admin">Yönetici</option>
          </select></label>
        </div>
        {error && <div className="login-error">{error}</div>}
        <button
          className="primary wide"
          data-testid="user-save-button"
          disabled={!form.name || !form.email || (!isEdit && form.password.length < 6) || (isEdit && form.password && form.password.length < 6) || saving}
          onClick={submit}
        >
          {saving ? "Kaydediliyor..." : (isEdit ? "Değişiklikleri kaydet" : "Personeli ekle")}
        </button>
      </div>
    </div>
  );
}

// ============ Installment (Taksitli Ödeme) Calculator ============
const INSTALLMENT_RATES = [
  { n: 1, label: "Tek Çekim", rate: 0.0285 },
  { n: 2, label: "2 Taksit", rate: 0.0589 },
  { n: 3, label: "3 Taksit", rate: 0.0795 },
  { n: 4, label: "4 Taksit", rate: 0.0988 },
  { n: 5, label: "5 Taksit", rate: 0.1172 },
  { n: 6, label: "6 Taksit", rate: 0.1347 },
];

function Installments() {
  const [netAmount, setNetAmount] = useState("");
  const [selected, setSelected] = useState(1);
  const net = parseFloat(String(netAmount).replace(",", ".")) || 0;
  const presets = [1000, 2500, 5000, 10000, 25000];

  const rows = INSTALLMENT_RATES.map((r) => {
    const gross = net > 0 ? net / (1 - r.rate) : 0;
    const monthly = gross > 0 ? gross / r.n : 0;
    return { ...r, gross, monthly };
  });
  const active = rows.find((r) => r.n === selected) || rows[0];

  return (
    <>
      <PageHead
        eyebrow="POS Hesaplama"
        title="Taksitli Ödeme"
        sub="Elinize geçmesini istediğiniz net tutarı girin, müşteriye yansıtılacak taksitli toplamı anında görün."
      />
      <section style={{ background: "linear-gradient(135deg,#0d1b3f 0%,#1e40af 55%,#6d28d9 100%)", borderRadius: 20, padding: "30px 28px", boxShadow: "0 20px 50px rgba(13,27,63,.28)", color: "#fff", marginBottom: 22, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle at 30% 30%,rgba(255,255,255,0.14),transparent 60%)" }} />
        <div style={{ position: "absolute", left: -40, bottom: -40, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle at 50% 50%,rgba(109,40,217,.35),transparent 65%)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", opacity: 0.75, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <CreditCard size={16} /> Hesaba geçecek net tutar
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <input
              data-testid="installment-net-input"
              type="number"
              inputMode="decimal"
              min="0"
              value={netAmount}
              onChange={(e) => setNetAmount(e.target.value)}
              placeholder="0"
              autoFocus
              style={{
                background: "transparent", border: "none", outline: "none",
                fontSize: 56, fontWeight: 800, color: "#fff", width: "min(360px, 100%)",
                padding: 0, fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em"
              }}
            />
            <span style={{ fontSize: 32, fontWeight: 700, opacity: 0.85 }}>₺</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
            {presets.map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`installment-preset-${v}`}
                onClick={() => setNetAmount(String(v))}
                style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", backdropFilter: "blur(8px)" }}
              >{new Intl.NumberFormat("tr-TR").format(v)} ₺</button>
            ))}
            {netAmount && (
              <button type="button" data-testid="installment-clear" onClick={() => setNetAmount("")} style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>Temizle</button>
            )}
          </div>
        </div>
      </section>

      {net > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginBottom: 24 }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "2px solid #1e40af", boxShadow: "0 8px 24px rgba(30,64,175,.14)" }}>
              <div style={{ fontSize: 11, color: "#6a7080", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 700 }}>Müşteriye yansıyacak toplam</div>
              <div data-testid="installment-total" style={{ fontSize: 34, fontWeight: 800, color: "#0d1b3f", marginTop: 6, letterSpacing: "-.02em" }}>{money(active.gross)}</div>
              <div style={{ fontSize: 12, color: "#6a7080", marginTop: 4 }}>{active.label} · vade farkı %{(active.rate * 100).toFixed(2).replace(".", ",")}</div>
            </div>
            <div style={{ background: "linear-gradient(135deg,#0d9f4a,#059669)", borderRadius: 16, padding: "20px 22px", color: "#fff", boxShadow: "0 8px 24px rgba(13,159,74,.28)" }}>
              <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 700 }}>Aylık ödeme</div>
              <div data-testid="installment-monthly" style={{ fontSize: 34, fontWeight: 800, marginTop: 6, letterSpacing: "-.02em" }}>{money(active.monthly)}</div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>{active.n === 1 ? "Tek çekim" : `${active.n} ay boyunca aynı tutar`}</div>
            </div>
            <div style={{ background: "linear-gradient(135deg,#fef3c7,#fde68a)", borderRadius: 16, padding: "20px 22px", border: "1px solid #fbbf24" }}>
              <div style={{ fontSize: 11, color: "#78350f", textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 700 }}>Vade farkı (müşteri)</div>
              <div data-testid="installment-fee" style={{ fontSize: 30, fontWeight: 800, color: "#78350f", marginTop: 6, letterSpacing: "-.02em" }}>{money(active.gross - net)}</div>
              <div style={{ fontSize: 12, color: "#78350f", marginTop: 4 }}>Sizin elinize {money(net)} geçer</div>
            </div>
          </div>

          <section className="panel" style={{ padding: 22 }}>
            <div className="table-head" style={{ gridTemplateColumns: "2fr 1fr 1.4fr 1.4fr" }}>
              <span>Seçenek</span><span>Oran</span><span>Aylık ödeme</span><span>Toplam yansıyacak</span>
            </div>
            {rows.map((r) => (
              <div
                key={r.n}
                className="table-row"
                data-testid={`installment-row-${r.n}`}
                onClick={() => setSelected(r.n)}
                style={{ gridTemplateColumns: "2fr 1fr 1.4fr 1.4fr", cursor: "pointer", background: r.n === selected ? "#e6f0ff" : undefined, border: r.n === selected ? "1px solid #a8c8f0" : "1px solid transparent", borderRadius: 10 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid " + (r.n === selected ? "#1e40af" : "#cbd2e0"), background: r.n === selected ? "#1e40af" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {r.n === selected && <Check size={11} color="#fff" />}
                  </span>
                  <strong>{r.label}</strong>
                </div>
                <div>%{(r.rate * 100).toFixed(2).replace(".", ",")}</div>
                <div style={{ fontWeight: 600 }}>{money(r.monthly)}</div>
                <div style={{ fontWeight: 700, color: "#1e40af" }}>{money(r.gross)}</div>
              </div>
            ))}
            <p style={{ fontSize: 12, color: "#6a7080", marginTop: 12 }}>
              Hesaplama: toplam = net tutar ÷ (1 − oran). Vade farkı müşteriye yansıtılır, elinize net tutar geçer.
            </p>
          </section>
        </>
      )}
      {net <= 0 && (
        <div className="empty-state" data-testid="installment-empty" style={{ marginTop: 4 }}>
          <CreditCard size={30} />
          <strong>Tutarı girin</strong>
          <span>Elinize geçmesini istediğiniz net tutarı yazdığınızda tüm taksit seçenekleri anında hesaplanır.</span>
        </div>
      )}
    </>
  );
}

// ============ Staff Payments (Ödeme Al / Tahsilat) ============
const PAY_METHODS = [
  { id: "nakit", label: "Nakit", color: "#0d9f4a" },
  { id: "kart", label: "Kredi Kartı", color: "#1e40af" },
  { id: "veresiye", label: "Veresiye / Açık Hesap", color: "#b45309" },
];

function StaffPayments({ user, jobs, payments, onReload, notify }) {
  const [form, setForm] = useState({ amount: "", method: "nakit", customer: "", company: "Lokalnet", job_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const openJobs = jobs.filter((j) => j.status !== "Tamamlandı" && (user.role === "admin" || (j.staff_ids || []).includes(user.id) || j.staff_id === user.id || j.staff === user.name));
  const mine = payments.filter((p) => p.collected_by_id === user.id);

  const pickJob = (jobId) => {
    const j = jobs.find((x) => x.id === jobId);
    setForm((f) => ({
      ...f,
      job_id: jobId,
      customer: j ? j.customer : f.customer,
      company: j?.company || f.company,
      amount: j ? String(j.price || "") : f.amount,
    }));
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0 || !form.customer.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/payments`, {
        amount: Number(form.amount), method: form.method, customer: form.customer.trim(),
        company: form.company, job_id: form.job_id || null, notes: form.notes,
      });
      setForm({ amount: "", method: "nakit", customer: "", company: "Lokalnet", job_id: "", notes: "" });
      await onReload();
      notify("Tahsilat kaydedildi");
    } catch (e) { notify(errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHead eyebrow="Saha tahsilatı" title="Ödeme Al" sub="Sahada müşteriden alınan ödemeleri anında kaydedin — yönetici paneline düşer." />
      <div className="section-row">
        <section className="panel" style={{ padding: 22 }}>
          <h3 style={{ margin: "0 0 12px" }}>Yeni tahsilat</h3>
          <div className="form-grid">
            <label>Bağlı iş (opsiyonel)
              <select data-testid="payment-job-select" value={form.job_id} onChange={(e) => pickJob(e.target.value)} style={{ padding: "11px 12px", borderRadius: 10, border: "1px solid #e6e8ef" }}>
                <option value="">İşe bağlama</option>
                {openJobs.map((j) => <option key={j.id} value={j.id}>{j.title} — {j.customer}</option>)}
              </select>
            </label>
            <label>Müşteri<input data-testid="payment-customer-input" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} placeholder="Müşteri adı" /></label>
            <label>Tutar (₺)<input data-testid="payment-amount-input" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" /></label>
            <label>Şirket / Hizmet
              <select data-testid="payment-company-select" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} style={{ padding: "11px 12px", borderRadius: 10, border: "1px solid #e6e8ef" }}>
                <option value="Gibirnet">Gibirnet</option>
                <option value="Lokalnet">Lokalnet</option>
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, margin: "12px 0", flexWrap: "wrap" }}>
            {PAY_METHODS.map((m) => (
              <button
                key={m.id} type="button" data-testid={`payment-method-${m.id}`}
                onClick={() => setForm({ ...form, method: m.id })}
                style={{ flex: "1 1 120px", padding: "12px 10px", borderRadius: 12, border: "2px solid " + (form.method === m.id ? m.color : "#e6e8ef"), background: form.method === m.id ? m.color + "14" : "#fff", color: form.method === m.id ? m.color : "#6a7080", fontWeight: 700, cursor: "pointer" }}
              >{m.label}</button>
            ))}
          </div>
          <label style={{ display: "block", marginBottom: 12 }}>Not (opsiyonel)<input data-testid="payment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Örn. Kurulum ücreti tahsil edildi" /></label>
          <button className="primary wide" data-testid="payment-save-button" disabled={saving || !form.amount || Number(form.amount) <= 0 || !form.customer.trim()} onClick={save}>
            <Banknote size={17} /> {saving ? "Kaydediliyor..." : "Tahsilatı kaydet"}
          </button>
        </section>
        <section className="panel">
          <div className="panel-head"><div><h2>Aldığım ödemeler</h2><p>{mine.length} kayıt</p></div></div>
          {mine.length ? mine.map((p) => (
            <div className="table-row" key={p.id} data-testid={`my-payment-${p.id}`} style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr" }}>
              <div><strong>{p.customer}</strong><span className="muted">{new Date(p.created_at).toLocaleString("tr-TR")}</span></div>
              <div><CompanyTag company={p.company} /></div>
              <div><span className="tag" style={{ background: (PAY_METHODS.find((m) => m.id === p.method)?.color || "#666") + "14", color: PAY_METHODS.find((m) => m.id === p.method)?.color }}>{PAY_METHODS.find((m) => m.id === p.method)?.label || p.method}</span></div>
              <strong style={{ textAlign: "right" }}>{money(p.amount)}</strong>
            </div>
          )) : <div className="empty-state"><Banknote size={30} /><strong>Henüz tahsilat yok</strong><span>İlk ödemeyi soldaki formdan kaydedin.</span></div>}
        </section>
      </div>
    </>
  );
}

// ============ Collections (Alınan Ödemeler — admin) ============
function Collections({ payments, onReload }) {
  const total = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const byMethod = PAY_METHODS.map((m) => ({ ...m, sum: payments.filter((p) => p.method === m.id).reduce((s, p) => s + Number(p.amount || 0), 0), count: payments.filter((p) => p.method === m.id).length }));
  return (
    <>
      <PageHead eyebrow="Kasa" title="Alınan Ödemeler" sub="Saha personelinin yaptığı tüm tahsilatlar anlık olarak burada listelenir."
        action={<button className="secondary" data-testid="collections-refresh" onClick={onReload}><RefreshCw size={15} /> Yenile</button>} />
      <div className="metric-grid" style={{ marginBottom: 16 }}>
        <Metric label="Toplam tahsilat" value={money(total)} detail={`${payments.length} işlem`} color="green" />
        {byMethod.map((m) => <Metric key={m.id} label={m.label} value={money(m.sum)} detail={`${m.count} işlem`} color={m.id === "nakit" ? "green" : m.id === "kart" ? "blue" : "orange"} />)}
      </div>
      <section className="panel jobs-panel">
        <div className="table-head" style={{ gridTemplateColumns: "1.2fr 1.4fr 1fr 1fr 1fr 1fr" }}>
          <span>Personel</span><span>Müşteri</span><span>Şirket</span><span>Tip</span><span>Tarih</span><span>Tutar</span>
        </div>
        {payments.length ? payments.map((p) => (
          <div className="table-row" key={p.id} data-testid={`collection-${p.id}`} style={{ gridTemplateColumns: "1.2fr 1.4fr 1fr 1fr 1fr 1fr" }}>
            <div><div className="avatar small">{initials(p.collected_by || "")}</div><strong>{p.collected_by}</strong></div>
            <div><strong>{p.customer}</strong>{p.notes && <span className="muted">{p.notes}</span>}</div>
            <div><CompanyTag company={p.company} /></div>
            <div><span className="tag" style={{ background: (PAY_METHODS.find((m) => m.id === p.method)?.color || "#666") + "14", color: PAY_METHODS.find((m) => m.id === p.method)?.color }}>{PAY_METHODS.find((m) => m.id === p.method)?.label || p.method}</span></div>
            <div><span className="muted">{new Date(p.created_at).toLocaleString("tr-TR")}</span></div>
            <strong style={{ textAlign: "right", color: "#0d6032" }}>{money(p.amount)}</strong>
          </div>
        )) : <div className="empty-state"><Banknote size={30} /><strong>Henüz tahsilat kaydı yok</strong><span>Personel "Ödeme Al" ekranından kayıt girdiğinde burada görünür.</span></div>}
      </section>
    </>
  );
}

// ============ Finance (Gelir / Gider — admin) ============
const FINANCE_CATS = {
  income: ["Satış geliri", "Hizmet geliri", "Diğer gelir"],
  expense: ["Kira", "Fatura (elektrik/su/internet)", "Maaş", "Tedarikçi ödemesi", "Yakıt/araç", "Vergi", "Diğer gider"],
};

function Finance({ data, onReload, notify }) {
  const [form, setForm] = useState({ kind: "expense", category: FINANCE_CATS.expense[0], amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const [month, setMonth] = useState("");
  const [saving, setSaving] = useState(false);
  const items = data?.items || [];
  const totals = data?.totals || { income: 0, expense: 0, net: 0 };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/finance`, { ...form, amount: Number(form.amount) });
      setForm({ ...form, amount: "", description: "" });
      await onReload();
      notify("Kayıt eklendi");
    } catch (e) { notify(errText(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/finance/${id}`); await onReload(); notify("Kayıt silindi"); }
    catch (e) { notify(errText(e)); }
  };

  return (
    <>
      <PageHead eyebrow="Ön muhasebe" title="Gelir / Gider Yönetimi" sub="İşletmenin genel gelir ve giderlerini kategori bazlı kaydedin."
        action={<input type="month" data-testid="finance-month-filter" value={month} onChange={async (e) => { setMonth(e.target.value); try { const r = await axios.get(`${API}/finance${e.target.value ? `?month=${e.target.value}` : ""}`); } catch { /* noop */ } }} style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e6e8ef" }} />} />
      <div className="metric-grid" style={{ marginBottom: 16 }}>
        <Metric label="Toplam gelir" value={money(totals.income)} detail="Kayıtlı" color="green" />
        <Metric label="Toplam gider" value={money(totals.expense)} detail="Kayıtlı" color="orange" />
        <Metric label="Net durum" value={money(totals.net)} detail={totals.net >= 0 ? "Artıda" : "Ekside"} color={totals.net >= 0 ? "navy" : "orange"} />
      </div>
      <div className="section-row">
        <section className="panel" style={{ padding: 22 }}>
          <h3 style={{ margin: "0 0 12px" }}>Yeni kayıt</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {["expense", "income"].map((k) => (
              <button key={k} type="button" data-testid={`finance-kind-${k}`} onClick={() => setForm({ ...form, kind: k, category: FINANCE_CATS[k][0] })}
                style={{ flex: 1, padding: "12px", borderRadius: 12, border: "2px solid " + (form.kind === k ? (k === "expense" ? "#dc2626" : "#0d9f4a") : "#e6e8ef"), background: form.kind === k ? (k === "expense" ? "#fef2f2" : "#e6f7ec") : "#fff", color: form.kind === k ? (k === "expense" ? "#b91c1c" : "#0d6032") : "#6a7080", fontWeight: 700, cursor: "pointer" }}>
                {k === "expense" ? "Gider" : "Gelir"}
              </button>
            ))}
          </div>
          <div className="form-grid">
            <label>Kategori
              <select data-testid="finance-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ padding: "11px 12px", borderRadius: 10, border: "1px solid #e6e8ef" }}>
                {FINANCE_CATS[form.kind].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Tutar (₺)<input data-testid="finance-amount" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" /></label>
            <label>Tarih<input data-testid="finance-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
            <label>Açıklama<input data-testid="finance-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opsiyonel not" /></label>
          </div>
          <button className="primary wide" style={{ marginTop: 12 }} data-testid="finance-save" disabled={saving || !form.amount || Number(form.amount) <= 0} onClick={save}>
            <Wallet size={17} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </section>
        <section className="panel">
          <div className="panel-head"><div><h2>Kayıtlar</h2><p>{items.length} satır</p></div></div>
          {items.length ? items.map((f) => (
            <div className="table-row" key={f.id} data-testid={`finance-row-${f.id}`} style={{ gridTemplateColumns: "1fr 1.4fr 1fr 1fr auto" }}>
              <div><span className="tag" style={{ background: f.kind === "expense" ? "#fee2e2" : "#e6f7ec", color: f.kind === "expense" ? "#b91c1c" : "#0d6032" }}>{f.kind === "expense" ? "Gider" : "Gelir"}</span></div>
              <div><strong>{f.category}</strong>{f.description && <span className="muted">{f.description}</span>}</div>
              <div><span className="muted">{f.date}</span></div>
              <strong style={{ textAlign: "right", color: f.kind === "expense" ? "#b91c1c" : "#0d6032" }}>{f.kind === "expense" ? "−" : "+"}{money(f.amount)}</strong>
              <button className="icon-danger" data-testid={`finance-delete-${f.id}`} onClick={() => remove(f.id)} title="Sil"><Trash2 size={13} /></button>
            </div>
          )) : <div className="empty-state"><Wallet size={30} /><strong>Kayıt yok</strong><span>Soldaki formdan ilk gelir/gider kaydını ekleyin.</span></div>}
        </section>
      </div>
    </>
  );
}

// ============ Scanner ============
function Scanner({ products, onClose, onAdd, onReload, isAdmin }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [code, setCode] = useState("");
  const [detected, setDetected] = useState(null); // scanned code awaiting confirm
  const [cameraError, setCameraError] = useState("");
  const [newForm, setNewForm] = useState(null); // {name, sku, barcode, purchase_price, price, stock, low_stock, unit}
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [quickMode, setQuickMode] = useState(false);
  const [quickHits, setQuickHits] = useState([]); // {name, at}
  const lastQuickRef = useRef({ code: "", at: 0 });
  const productsRef = useRef(products);
  useEffect(() => { productsRef.current = products; }, [products]);

  const productByBarcode = (bc) =>
    productsRef.current.find((p) => String(p.barcode || "").trim() === String(bc || "").trim());

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      osc.type = "square";
      gain.gain.value = 0.06;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close(); }, 90);
    } catch { /* ignore */ }
  };

  // Start camera scanning on mount
  useEffect(() => {
    let active = true;
    (async () => {
      // getUserMedia gerektirir: HTTPS (veya localhost)
      const secure = window.isSecureContext || location.hostname === "localhost";
      if (!secure) {
        setCameraError("Kamera yalnızca HTTPS üzerinden çalışır. Site adresinin https:// ile başladığından emin olun.");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError("Tarayıcı kamera API'sini desteklemiyor. Chrome/Safari güncel sürümü deneyin.");
        return;
      }
      try {
        // Önce izin al: iOS Safari device labels iznden sonra doluyor
        try {
          const permStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
          permStream.getTracks().forEach((t) => t.stop());
        } catch (permErr) {
          const name = permErr?.name || "";
          if (name === "NotAllowedError" || name === "PermissionDeniedError") {
            setCameraError("Kamera izni reddedildi. Tarayıcı adres çubuğundaki kilit simgesinden kamera iznini açın ve sayfayı yenileyin.");
          } else if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
            setCameraError("Cihazda kullanılabilir kamera bulunamadı. Manuel giriş kullanabilirsiniz.");
          } else {
            setCameraError("Kamera açılamadı: " + (permErr?.message || name || "bilinmeyen hata"));
          }
          return;
        }
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        let deviceId = null;
        try {
          const devices = await BrowserMultiFormatReader.listVideoInputDevices();
          const back = devices.find((d) => /back|rear|environment|arka/i.test(d.label));
          deviceId = back?.deviceId || devices[0]?.deviceId || null;
        } catch { /* ignore */ }
        let controls;
        if (deviceId) {
          controls = await reader.decodeFromVideoDevice(deviceId, videoRef.current, (result) => {
            if (!active || !result) return;
            const text = result.getText();
            if (!text) return;
            setDetected(text);
            setCode(text);
          });
        } else {
          // Cihaz listesi boşsa doğrudan constraints ile başlat (Safari fallback)
          controls = await reader.decodeFromConstraints(
            { video: { facingMode: { ideal: "environment" } }, audio: false },
            videoRef.current,
            (result) => {
              if (!active || !result) return;
              const text = result.getText();
              if (!text) return;
              setDetected(text);
              setCode(text);
            }
          );
        }
        if (!active) { controls?.stop(); return; }
        controlsRef.current = controls;
      } catch (e) {
        setCameraError("Kamera başlatılamadı: " + (e?.message || "bilinmeyen hata"));
      }
    })();
    return () => {
      active = false;
      try { controlsRef.current?.stop(); } catch { /* ignore */ }
    };
  }, []);

  // Auto-add in quick mode when a known product is detected (debounced against same code within 1.5s)
  useEffect(() => {
    if (!quickMode || !detected) return;
    const p = productByBarcode(detected);
    if (!p) return;
    const now = Date.now();
    if (lastQuickRef.current.code === detected && (now - lastQuickRef.current.at) < 1500) return;
    lastQuickRef.current = { code: detected, at: now };
    onAdd(p);
    beep();
    setQuickHits((prev) => [{ name: p.name, at: now }, ...prev].slice(0, 8));
    // Clear detected state so the next scan of same code (after debounce window) is fresh
    setTimeout(() => setDetected(null), 400);
  }, [detected, quickMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const found = detected ? productByBarcode(detected) : (code ? productByBarcode(code) : null);

  const confirmAdd = (p) => {
    onAdd(p);
    if (quickMode) {
      beep();
      setQuickHits((prev) => [{ name: p.name, at: Date.now() }, ...prev].slice(0, 8));
      setDetected(null);
      setCode("");
    } else {
      onClose();
    }
  };

  const startNewProduct = () => {
    setNewForm({
      name: "", sku: "", barcode: detected || code || "",
      purchase_price: 0, price: 0, stock: 1, low_stock: 5, unit: "adet",
    });
  };

  const saveNewProduct = async () => {
    setSaving(true); setSaveError("");
    try {
      const payload = {
        ...newForm,
        stock: Number(newForm.stock),
        price: Number(newForm.price),
        purchase_price: Number(newForm.purchase_price),
        low_stock: Number(newForm.low_stock),
      };
      const r = await axios.post(`${API}/products`, payload);
      if (onReload) await onReload();
      // Add the freshly created product to cart
      onAdd(r.data);
      if (quickMode) {
        beep();
        setQuickHits((prev) => [{ name: r.data.name, at: Date.now() }, ...prev].slice(0, 8));
        setNewForm(null);
        setDetected(null);
        setCode("");
      } else {
        onClose();
      }
    } catch (e) { setSaveError(errText(e)); }
    finally { setSaving(false); }
  };

  // Auto-confirm on manual barcode input match
  const onManualChange = (v) => {
    setCode(v);
    setDetected(null);
    setNewForm(null);
    setSaveError("");
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal scanner-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" data-testid="scanner-close-button" onClick={onClose}><X /></button>
        <div className="modal-title" style={{ marginBottom: 12 }}>
          <div><span className="eyebrow">Karekod / Barkod</span><h2>Ürün okut</h2></div>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: quickMode ? "#e6f7ec" : "#f6f7fb", border: "1px solid " + (quickMode ? "#a8e0b9" : "#e6e8ef"), borderRadius: 10, marginBottom: 12, cursor: "pointer" }} data-testid="quick-mode-label">
          <input type="checkbox" data-testid="quick-mode-toggle" checked={quickMode} onChange={(e) => setQuickMode(e.target.checked)} style={{ width: 18, height: 18 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#0d1b3f" }}>Hızlı sepet modu</div>
            <div style={{ fontSize: 12, color: "#6a7080" }}>Her okuma otomatik sepete eklenir, pop-up kapanmaz. Kasada seri tarama için.</div>
          </div>
          {quickMode && <span data-testid="quick-mode-counter" style={{ background: "#0d9f4a", color: "#fff", padding: "4px 10px", borderRadius: 999, fontWeight: 700, fontSize: 13 }}>{quickHits.length}</span>}
        </label>

        {!newForm && (
          <>
            <div className="scanner-view" style={{ position: "relative", overflow: "hidden" }}>
              <video ref={videoRef} data-testid="scanner-video" style={{ width: "100%", maxHeight: 260, objectFit: "cover", borderRadius: 12, background: "#000" }} muted playsInline />
              {cameraError && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", color: "#fff", padding: 12, textAlign: "center", borderRadius: 12 }}>
                  <ScanLine size={30} />
                  <small style={{ marginTop: 8 }}>{cameraError}</small>
                </div>
              )}
              {!cameraError && (
                <span style={{ position: "absolute", bottom: 8, left: 0, right: 0, textAlign: "center", color: "#fff", fontSize: 12, textShadow: "0 1px 3px #000" }}>Kamerayı barkoda / karekoda doğrultun</span>
              )}
            </div>

            <div className="scan-input" style={{ marginTop: 12 }}>
              <input data-testid="barcode-input" placeholder="veya barkod numarasını yazın" value={code} onChange={(e) => onManualChange(e.target.value)} />
              {found && !quickMode && <button data-testid="barcode-add-button" className="primary" onClick={() => confirmAdd(found)}>Onayla & Ekle</button>}
              {found && quickMode && <button data-testid="barcode-add-button" className="primary" onClick={() => confirmAdd(found)}>Ekle</button>}
            </div>

            {found && !quickMode && (
              <div className="found-product" data-testid="scanner-found-product">
                <Package size={18} />
                <div style={{ flex: 1 }}>
                  <strong>{found.name}</strong>
                  <div style={{ fontSize: 11, color: "#6a7080", marginTop: 2 }}>Net {money(found.price)} · KDV %20</div>
                </div>
                <span style={{ fontWeight: 700, color: "#1e40af" }} data-testid="scanner-price-vat">{money(found.price * 1.20)}</span>
              </div>
            )}

            {quickMode && quickHits.length > 0 && (
              <div data-testid="quick-hits-list" style={{ marginTop: 12, maxHeight: 160, overflowY: "auto", border: "1px solid #e6e8ef", borderRadius: 10 }}>
                {quickHits.map((h, i) => (
                  <div key={h.at + "-" + i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: i === quickHits.length - 1 ? "none" : "1px solid #f0f2f7" }}>
                    <Check size={16} color="#0d9f4a" />
                    <strong style={{ flex: 1, fontSize: 14 }}>{h.name}</strong>
                    <span style={{ fontSize: 11, color: "#6a7080" }}>{new Date(h.at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                  </div>
                ))}
              </div>
            )}

            {(detected || code) && !found && (
              <div style={{ marginTop: 14, padding: 14, background: "#fff8e6", border: "1px solid #f3d38a", borderRadius: 10 }}>
                <p data-testid="barcode-not-found" style={{ margin: 0, fontWeight: 600, color: "#7a5a00" }}>Bu barkod ilk defa okundu.</p>
                <small style={{ color: "#7a5a00" }}>Barkod: <code>{detected || code}</code></small>
                {isAdmin ? (
                  <button data-testid="scanner-new-product-button" className="primary wide" style={{ marginTop: 10 }} onClick={startNewProduct}>
                    <Plus size={16} /> Yeni ürün olarak ekle
                  </button>
                ) : (
                  <p style={{ margin: "8px 0 0", color: "#7a5a00", fontSize: 13 }}>Yönetici bu ürünü eklemeden satışa çıkarılamaz.</p>
                )}
              </div>
            )}

            {quickMode && (
              <button className="secondary wide" style={{ marginTop: 12 }} data-testid="quick-mode-finish" onClick={onClose}>
                Taramayı bitir ({quickHits.length} ürün)
              </button>
            )}
          </>
        )}

        {newForm && (
          <div>
            <div className="modal-title" style={{ marginBottom: 8 }}>
              <div><span className="eyebrow">Yeni ürün</span><h3 style={{ margin: 0 }}>Ürün bilgilerini girin</h3></div>
            </div>
            <div className="form-grid">
              <label>Ürün adı<input data-testid="new-product-name" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} autoFocus /></label>
              <label>SKU / Kod<input data-testid="new-product-sku" value={newForm.sku} onChange={(e) => setNewForm({ ...newForm, sku: e.target.value })} /></label>
              <label>Barkod<input data-testid="new-product-barcode" value={newForm.barcode} onChange={(e) => setNewForm({ ...newForm, barcode: e.target.value })} /></label>
              <label>Birim<input data-testid="new-product-unit" value={newForm.unit} onChange={(e) => setNewForm({ ...newForm, unit: e.target.value })} /></label>
              <label>Alış (₺)<input data-testid="new-product-purchase" type="number" value={newForm.purchase_price} onChange={(e) => setNewForm({ ...newForm, purchase_price: e.target.value })} /></label>
              <label>Satış (₺)<input data-testid="new-product-price" type="number" value={newForm.price} onChange={(e) => setNewForm({ ...newForm, price: e.target.value })} /></label>
              <label>Stok<input data-testid="new-product-stock" type="number" value={newForm.stock} onChange={(e) => setNewForm({ ...newForm, stock: e.target.value })} /></label>
              <label>Kritik eşik<input data-testid="new-product-lowstock" type="number" value={newForm.low_stock} onChange={(e) => setNewForm({ ...newForm, low_stock: e.target.value })} /></label>
            </div>
            {saveError && <div className="login-error" data-testid="new-product-error">{saveError}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="secondary" onClick={() => setNewForm(null)} data-testid="new-product-cancel">Vazgeç</button>
              <button data-testid="new-product-save" className="primary" disabled={!newForm.name || !newForm.sku || saving} onClick={saveNewProduct} style={{ flex: 1 }}>
                {saving ? "Kaydediliyor..." : "Kaydet & Sepete ekle"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Address → Coordinates (Nominatim) ============
async function geocodeAddress(address) {
  const q = (address || "").trim();
  if (!q) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=tr&limit=1&accept-language=tr&q=${encodeURIComponent(q)}`;
    const r = await fetch(url, { headers: { "Accept": "application/json" } });
    if (!r.ok) return null;
    const arr = await r.json();
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const hit = arr[0];
    return { lat: parseFloat(hit.lat), lng: parseFloat(hit.lon), display: hit.display_name || q };
  } catch {
    return null;
  }
}

// ============ Job Modal (admin only) ============
function JobModal({ onClose, onSave, staffList, currentUser, initial = null }) {
  // Admin can also be selected as an assignee (e.g. admin wants to take a job themselves)
  const assignable = currentUser?.role === "admin"
    ? [{ id: currentUser.id, name: currentUser.name + " (yönetici)" }, ...staffList]
    : staffList;
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    customer: initial?.customer || "",
    customer_phone: initial?.customer_phone || "",
    notify_customer: false, // düzenlemede SMS tekrar gönderilmez
    address: initial?.address || "",
    company: initial?.company || "", // zorunlu — boş bırakılırsa kaydedemez
    scheduled_at: initial?.scheduled_at || "09:00",
    scheduled_date: initial?.scheduled_date || new Date().toISOString().slice(0, 10),
    staff_ids: initial?.staff_ids ? [...initial.staff_ids] : [],
    staff_names: initial?.staff_names ? [...initial.staff_names] : [],
    price: initial?.price != null ? String(initial.price) : "",
    latitude: initial?.latitude != null ? String(initial.latitude) : "",
    longitude: initial?.longitude != null ? String(initial.longitude) : "",
  }));
  const [geo, setGeo] = useState({ loading: false, hit: null, tried: false });

  const runGeocode = async () => {
    if (!form.address?.trim()) return;
    setGeo({ loading: true, hit: null, tried: true });
    const r = await geocodeAddress(form.address);
    if (r && Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
      setForm((f) => ({ ...f, latitude: r.lat.toFixed(6), longitude: r.lng.toFixed(6) }));
      setGeo({ loading: false, hit: r, tried: true });
    } else {
      setGeo({ loading: false, hit: null, tried: true });
    }
  };

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const toggleStaff = (s) => {
    const has = form.staff_ids.includes(s.id);
    if (has) {
      setForm({
        ...form,
        staff_ids: form.staff_ids.filter((id) => id !== s.id),
        staff_names: form.staff_names.filter((n) => n !== s.name),
      });
    } else {
      setForm({
        ...form,
        staff_ids: [...form.staff_ids, s.id],
        staff_names: [...form.staff_names, s.name],
      });
    }
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) return alert("Bu tarayıcıda konum servisi yok");
    navigator.geolocation.getCurrentPosition(
      (p) => setForm((f) => ({ ...f, latitude: p.coords.latitude.toFixed(6), longitude: p.coords.longitude.toFixed(6) })),
      () => alert("Konum alınamadı. Adres alanına manuel koordinat girebilirsiniz."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const canSave = form.title && form.customer && form.staff_ids.length > 0 && form.company;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">{isEdit ? "Düzenle" : "Yeni kayıt"}</span><h2>{isEdit ? "İş planını düzenle" : "İş planla"}</h2></div>
          <button className="modal-close" data-testid="job-modal-close-button" onClick={onClose}><X /></button>
        </div>
        <div className="form-grid">
          <label>İş başlığı<input data-testid="job-title-input" name="title" value={form.title} onChange={set} placeholder="Örn. Kamera kurulumu" /></label>
          <label>Müşteri<input data-testid="job-customer-input" name="customer" value={form.customer} onChange={set} placeholder="Firma / müşteri adı" /></label>
          <label>Müşteri telefonu<input data-testid="job-customer-phone-input" name="customer_phone" value={form.customer_phone} onChange={set} placeholder="05xxxxxxxxx" /></label>
          <label>Adres<input data-testid="job-address-input" name="address" value={form.address} onChange={set} placeholder="İl / ilçe / adres" /></label>
          <label>Ücret (₺)<input data-testid="job-price-input" type="number" name="price" value={form.price} onChange={set} placeholder="0" /></label>
          <label>Tarih<input data-testid="job-date-input" type="date" name="scheduled_date" value={form.scheduled_date} onChange={set} /></label>
          <label>Saat<input data-testid="job-time-input" type="time" name="scheduled_at" value={form.scheduled_at} onChange={set} /></label>
          <label>
            Şirket / Hizmet türü <em style={{ color: "#b91c1c" }}>*</em>
            <select data-testid="job-company-select" name="company" value={form.company} onChange={set} style={{ padding: "11px 12px", borderRadius: 10, border: "2px solid " + (form.company ? (form.company === "Lokalnet" ? "#1e40af" : "#7c3aed") : "#e6e8ef"), background: form.company ? (form.company === "Lokalnet" ? "#e6f0ff" : "#f3ecff") : "#fff", fontWeight: 700 }}>
              <option value="" disabled>Seçiniz...</option>
              <option value="Gibirnet">Gibirnet — İnternet kurulum / teknik destek</option>
              <option value="Lokalnet">Lokalnet — Kamera & güvenlik sistemleri</option>
            </select>
          </label>
        </div>
        {!form.company && <div data-testid="job-company-required" style={{ fontSize: 12, color: "#b91c1c", margin: "-4px 0 10px" }}>Şirket / hizmet türü seçimi zorunludur.</div>}

        {!isEdit && (
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: form.notify_customer ? "#e6f0ff" : "#f6f7fb", border: "1px solid " + (form.notify_customer ? "#a8c8f0" : "#e6e8ef"), borderRadius: 10, marginBottom: 12, cursor: form.customer_phone?.trim() ? "pointer" : "not-allowed", opacity: form.customer_phone?.trim() ? 1 : 0.55 }} data-testid="job-notify-customer-label">
          <input type="checkbox" data-testid="job-notify-customer-checkbox" checked={form.notify_customer} disabled={!form.customer_phone?.trim()} onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })} style={{ width: 18, height: 18, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#0d1b3f" }}>Müşteriye SMS gönder</div>
            <div style={{ fontSize: 12, color: "#6a7080", marginTop: 2 }}>İş emri oluşturulduğunda müşteriye otomatik bilgilendirme mesajı gider:</div>
            <div style={{ fontSize: 12, color: "#0d1b3f", fontStyle: "italic", background: "#fff", padding: "8px 10px", borderRadius: 8, marginTop: 6, border: "1px solid #e6e8ef" }}>
              "Sayın; {form.customer?.trim() || "{Müşteri Adı}"} iş emri kaydınız oluşturuldu, ekip arkadaşlarımız 48 saat içerisinde sizlere dönüş yapacaktır. Saygılarımızla iyi günler dileriz. LokalnetBT"
            </div>
            {!form.customer_phone?.trim() && <div style={{ fontSize: 11, color: "#a15c00", marginTop: 4 }}>Müşteri telefonu girilmeden SMS seçeneği aktif olmaz.</div>}
          </div>
        </label>
        )}

        <div className="staff-multi">
          <div className="staff-multi-head">
            <span>Personel <em>({form.staff_ids.length} seçili)</em></span>
            <small>Birden fazla personel seçebilirsiniz</small>
          </div>
          <div className="staff-chips">
            {assignable.length === 0 && <span className="empty-inline">Önce personel ekleyin</span>}
            {assignable.map((s) => {
              const active = form.staff_ids.includes(s.id);
              return (
                <button
                  type="button"
                  key={s.id}
                  className={active ? "staff-chip active" : "staff-chip"}
                  data-testid={`job-staff-chip-${s.id}`}
                  onClick={() => toggleStaff(s)}
                >
                  <div className="avatar small">{initials(s.name)}</div>
                  <span>{s.name}</span>
                  {active && <Check size={14} />}
                </button>
              );
            })}
          </div>
        </div>
        {/* Konum alanları gizli — adres girilince otomatik geocode yapılır, canlı takip için lat/lng arka planda tutulur */}
        <button type="button" style={{ display: "none" }} data-testid="job-geocode-button" onClick={runGeocode}><Navigation size={14} /></button>
        <button type="button" style={{ display: "none" }} data-testid="job-use-location" onClick={useMyLocation}><MapPin size={14} /></button>
        <button
          data-testid="job-save-button"
          className="primary wide"
          disabled={!canSave || geo.loading}
          onClick={async () => {
            // Adres yazıldıysa ve manuel lat/lng yoksa otomatik geocode yap
            let lat = form.latitude ? Number(form.latitude) : null;
            let lng = form.longitude ? Number(form.longitude) : null;
            if ((!lat || !lng) && form.address?.trim()) {
              setGeo({ loading: true, hit: null, tried: true });
              const r = await geocodeAddress(form.address);
              if (r && Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
                lat = r.lat; lng = r.lng;
                setForm((f) => ({ ...f, latitude: r.lat.toFixed(6), longitude: r.lng.toFixed(6) }));
                setGeo({ loading: false, hit: r, tried: true });
              } else {
                setGeo({ loading: false, hit: null, tried: true });
              }
            }
            onSave({
              ...form,
              staff_id: form.staff_ids[0] || null,
              staff: form.staff_names.join(", "),
              price: Number(form.price || 0),
              latitude: lat,
              longitude: lng,
            });
          }}
        >
          {isEdit ? "Değişiklikleri kaydet" : "İşi plana ekle"}
        </button>
      </div>
    </div>
  );
}

// ============ Invoice Modal ============
function InvoiceModal({ onClose, onSave, subtotal, tax, total }) {
  const [customer, setCustomer] = useState("");
  const [taxId, setTaxId] = useState("");
  const [address, setAddress] = useState("");
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal small-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div><span className="eyebrow">Belge</span><h2>Fatura oluştur</h2></div>
          <button className="modal-close" data-testid="invoice-modal-close-button" onClick={onClose}><X /></button>
        </div>
        <label>Müşteri / firma
          <input data-testid="invoice-customer-input" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Müşteri adını yazın" />
        </label>
        <label>VKN / TCKN (opsiyonel)
          <input data-testid="invoice-tax-input" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="1234567890" />
        </label>
        <label>Adres (opsiyonel)
          <input data-testid="invoice-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="İl / ilçe / adres" />
        </label>
        <div className="invoice-total-lines">
          <div><span>Ara toplam</span><strong>{money(subtotal)}</strong></div>
          <div><span>KDV (%20)</span><strong>{money(tax)}</strong></div>
          <div className="big"><span>Genel toplam</span><strong>{money(total)}</strong></div>
        </div>
        <button data-testid="invoice-save-button" className="primary wide" disabled={!customer || !total} onClick={() => onSave({ customer, taxId, address })}>
          <FileText size={17} /> Faturayı kaydet
        </button>
      </div>
    </div>
  );
}

export default App;
